'use strict';

/* eslint-disable max-len */
/* eslint-disable indent */

import React, {
    MouseEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import moment from 'moment-timezone';


// DOC: https://github.com/vkurko/calendar
import {
    Calendar as EventCalendar,
    createCalendar,
    DayGrid,
    Interaction,
    TimeGrid
} from '@event-calendar/core';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    LoadConfigParameters,
    LoadConfigResult,
    LoadDataParameters,
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping,
    SelectedViews
} from '~/shared/src/utilities/modules/calendar';
import {Query} from '~/shared/src/utilities/query';
import {
    DataDescriptor,
    DataStructure,
    getDataStructure
} from '~/shared/src/utilities/dataStructure';
import {CalendarEvent} from '~/shared/src/utilities/calendarEvent';
import {
    convertToBoolean,
    convertToDate,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';
import {valuePrecision} from '~/shared/src/utilities/modules/finder';
import messageUtilities from '~/shared/src/utilities/messageUtils';
import metaModel from '~/shared/src/utilities/metaModel';
import {isPropertyValueType} from '~/shared/src/mm/PropertySet';
import Item, {
    isItemOnlyNotRequiredAttributeForCreation,
    ItemAttributesForCreation,
    ItemAttributesSettable
} from '~/shared/src/ci/Item';
import Property from '~/shared/src/ci/Property';
import Reference, {
    ReferenceAttributesForCreation,
    ReferenceAttributesSettable
} from '~/shared/src/ci/Reference';
import ReferenceProperty from '~/shared/src/ci/ReferenceProperty';
import dataUtilities, {
    ItemData,
    ItemsAndReferencesCollection,
    MetaModelData
} from '~/shared/src/utilities/dataUtils';
import {ColumnFilter} from '~/shared/src/validationSchemes/modules/overview';

import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {Tooltip} from 'primereact/tooltip';

import Form, {
    AllProps as FormAllProps,
    ComponentProps as FormComponentProps,
    Variables
} from '~/frontend/src/components/base/Form';
import GenericForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    renderValue,
    resolveStyle
} from '~/frontend/src/utilities/valueRenderers';
import {
    getValueForDataDescriptorFromRowData,
    processData
} from '~/frontend/src/utilities/dataUtils';
import {
    getTextColorForBackgroundColor,
    getValueDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {getColorFromColorPalette} from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';
import {
    getPanelProps,
    getSubPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

import openPanelForItemType from '~/frontend/src/actions/panelManager/openPanelForItemType';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import loadData from '~/frontend/src/actions/data/loadData';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    DataIdList,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import clearPanelProps from '~/frontend/src/actions/panelManager/clearPanelProps';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';


type ExtendedProps = {
    background : boolean;
    calendarEvent : CalendarEvent;
    dataContext : string;
    dataStructure : DataStructure;
    rootItemSchema? : Schema;
    rootItemTypeName? : string;
    rootItemId? : number;
    rootItemKey? : string;
    editFormKey? : string;
    editFormTitle? : string;
    isDirty? : boolean;
};
type Data = {
    [ dataDescriptorAlias : string ] : any;
};
type StringData = {
    [ dataDescriptorAlias : string ] : string
};
type CalendarTooltip = {
    element : HTMLElement,
    content : React.ReactNode
};
type NewItemEventProps = {
    allDay : boolean;
    start : Date;
    end? : Date;
};
type EditableCalendarEvent = {
    calendarEvent : CalendarEvent;
    dataContext : string;
    dataStructure : DataStructure;
};

type LoadedDataIntervals = { start : string; end : string; }[];

type ResolvedEditFormKeys = {
    [ schemaAndItemTypeName : string ] : string;
};

type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    configLoaded? : boolean;
    dataLoaded? : boolean;
    loadedDataIntervals? : LoadedDataIntervals;
    events? : EventCalendar.Event[];
    metaData? : PanelElusiveDataState[ 'metaData' ] & {
        rowData? : LoadMetaDataRowData[];
        rowDataMapping? : LoadMetaDataRowDataMapping;
    };
    calendars? : {
        [ calendarKey : string ] : {
            data? : Partial<DataIdList>,
            metaData? : {
                rowData? : LoadMetaDataRowData[];
                rowDataMapping? : LoadMetaDataRowDataMapping;
            };
        }
    };
};

const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;
const setFormParam = origSetParam as <K extends keyof FormAllProps>( panelKey : string, paramKey : K, paramValue : FormAllProps[K] ) => SetParamAction;


export type DefaultProps = {
    schema? : Schema;

    /**
     * Is this component standalone.
     */
    mode? : 'standalone' | 'embedded';

    editable? : boolean;

    queryType? : Query[ 'type' ];

    nowIndicator? : boolean;
    viewSelectors? : SelectedViews[];
    flexibleSlotTimeLimits? : boolean;
    slotMinTime? : string;
    slotMaxTime? : string;

    filters? : ColumnFilter[],

    /**
     * The tabIndex offset for forms opened within the input field.
     */
    tabIndexOffset? : number;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];
};
export const defaultProps : Required<DefaultProps> = {
    additionalElusiveDataContexts: [],
    editable                     : false,
    flexibleSlotTimeLimits       : true,
    mode                         : 'standalone',
    nowIndicator                 : true,
    queryType                    : 'DataStructure',
    schema                       : 'DATA',
    slotMaxTime                  : '20:00:00',
    slotMinTime                  : '06:00:00',
    filters                      : [],
    tabIndexOffset               : 1,
    viewSelectors                : [ 'dayGridMonth', 'timeGridWeek', 'timeGridDay' ]
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    formUid? : string;

    /**
     * The key of the overview config.
     */
    viewKey : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    dataStructure? : DataStructure;

    dataDescriptors? : DataDescriptor[];

    calendarEvents? : CalendarEvent[];

    calendars? : {
        key : string;
        dataStructure : DataStructure;
        dataDescriptors : DataDescriptor[];
        calendarEvents : CalendarEvent[];
        schema : Schema;
        editable : boolean;
        queryType : Query[ 'type' ];
    }[];

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadConfigResult[ 'data' ]} config
     */
    onConfigLoad? : ( config : LoadConfigResult[ 'data' ] ) => void;

    /**
     * Function invoked if data was successfully loaded.
     */
    onDataLoad? : () => void;

    /**
     * Additional parameters which have to be relayed to the CFG:Query used to load the results.
     */
    additionalQueryParameters? : Required<LoadDataParameters[ 'parameters' ]>;

    /**
     * Additional parameters for the add item form.
     */
    additionalAddItemFormProps? : Partial<Omit<FormComponentProps, 'rootItemId' | 'rootItemKey' | 'rootItemType'>>;

    selectedView? : SelectedViews;
    startDate? : string;
    viewStartDate? : string;
    viewEndDate? : string;
    onRangeChange? : ( start : Date, end : Date ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


export type ReferenceType = {
    dirty : () => boolean;
    dirtyDataContexts : () => string[];
};


/**
 * Represents a Calendar method that is responsible for handling calendar-related logic
 * including calculating events, handling user interactions, and managing application state.
 *
 * @param {AllProps} props - The properties required to initialize and configure the calendar.
 * Includes settings, user data, calendars, events, and other options.
 *
 * @return {React.Element} Returns a React element that renders the calendar interface
 * and functionality with the provided configuration and state.
 */
const Calendar : React.FunctionComponent<AllProps> = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch          = useDispatch(),
          subPanelProps                   = useSelector( ( state : StoreState ) => {
              let retVal : AllProps | undefined = getSubPanelProps( props.uid, state ) as AllProps | undefined;

              if ( !retVal || !Object.keys( retVal ).length ) {
                  retVal = undefined;
              }

              return retVal;
          } ),
          formPersistentSubPanelPropsOrig = useSelector( ( state : StoreState ) => {
              let retVal : { [ uid : string ] : AllProps } | undefined = undefined;

              if ( props.formUid ) {
                  retVal = ( getPanelProps( props.formUid, state )?.panelProps ?? getSubPanelProps( props.formUid, state ) )?.persistentSubPanelProps;

                  if ( !retVal || !Object.keys( retVal ).length ) {
                      retVal = undefined;
                  }
              }

              return retVal;
          } ),
          formPersistentSubPanelProps     = useRef<any | undefined>( undefined );

    formPersistentSubPanelProps.current = formPersistentSubPanelPropsOrig;

    const setFormPersistentSubPanelProps = useCallback(
        <K extends keyof AllProps>( paramKey : K, paramValue : AllProps[K] ) => {
            if ( props.formUid && formPersistentSubPanelProps.current?.[ props.uid ]?.[ paramKey ] !== paramValue ) {
                formPersistentSubPanelProps.current = {
                    ...formPersistentSubPanelProps.current,
                    [ props.uid ]: {
                        ...formPersistentSubPanelProps.current?.[ props.uid ],
                        [ paramKey ]: paramValue
                    }
                };

                dispatch( setFormParam(
                    props.formUid,
                    'persistentSubPanelProps',
                    formPersistentSubPanelProps.current
                ) );
            }
        },
        [
            props.formUid,
            props.uid
        ]
    );

    props = useMemo(
        () => {
            return {
                ...props,
                ...subPanelProps,
                ...formPersistentSubPanelPropsOrig?.[ props.uid ]
            };
        },
        [
            props,
            subPanelProps,
            formPersistentSubPanelPropsOrig?.[ props.uid ]
        ]
    );

    const {
              additionalAddItemFormProps,
              additionalElusiveDataContexts,
              additionalQueryParameters,
              calendarEvents,
              calendars,
              configHash,
              dataContext,
              dataDescriptors,
              dataStructure,
              editable,
              filters,
              flexibleSlotTimeLimits,
              //mode,
              nowIndicator,
              onConfigLoad,
              onDataLoad,
              onRangeChange,
              queryType,
              schema,
              selectedView,
              slotMaxTime,
              slotMinTime,
              startDate,
              tabIndexOffset,
              uid,
              viewEndDate,
              viewKey,
              viewSelectors,
              viewStartDate
          }                                                       = props,
          editFormUid                                             = `${uid}-editItemDialogForm`,
          editFormDataContext                                     = `${editFormUid}-dataContext`,
          user                                                    = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                                 = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          elusiveData                                             = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          dataElusiveData                                         = useSelector( ( state : StoreState ) => state.elusiveData?.[ dataContext ] as ElusiveData | undefined ),
          editFormElusiveData                                     = useSelector( ( state : StoreState ) => state.elusiveData?.[ editFormUid ] as ElusiveData | undefined ),
          editFormProps                                           = useSelector( ( state : StoreState ) => {
              let retVal : PanelProps | undefined = getSubPanelProps( editFormUid, state );

              if ( !retVal || !Object.keys( retVal ).length ) {
                  retVal = undefined;
              }

              return retVal;
          } ),
          isLoading                                               = useRef<boolean>( false ),
          eventCalendar                                           = useRef<EventCalendar | null>( null ),
          editItem                                                = useRef<Item | null>( null ),
          editItemDataContext                                     = useRef<string>( dataContext ),
          dirtyDataContexts                                       = useRef<string[]>( [] ),
          timeoutId                                               = useRef<NodeJS.Timeout | undefined>(),
          [ tooltip, setTooltip ]                                 = useState<CalendarTooltip | undefined>( undefined ),
          [ showNewItemSelectDialog, setShowNewItemSelectDialog ] = useState<boolean>( false ),
          [ showEditItemDialog, setShowEditItemDialog ]           = useState<boolean>( false ),
          [ newItemEventProps, setNewItemEventProps ]             = useState<NewItemEventProps | null>( null ),
          [ selectedCalendarEvent, setSelectedCalendarEvent ]     = useState<number | undefined>( undefined ),
          [ editFormKey, setEditFormKey ]                         = useState<string | undefined>( undefined ),
          [ editFormTitle, setEditFormTitle ]                     = useState<string | undefined>( undefined ),
          [ editFormVariables, setEditFormVariables ]             = useState<Variables | undefined>( undefined ),
          [ forceRender, setForceRender ]                         = useState<number>( 0 ),
          debug                                                   = false,
          configRequestUid                                        = 'loadConfig',
          dataRequestUid                                          = 'loadData',
          globalDataVersions                                      = useMemo(
              () => {
                  return JSON.stringify( dataElusiveData?.data ?? {} );
              },
              [
                  dataElusiveData?.data
              ]
          ),
          calendarsDataVersions                                   = useMemo(
              () => {
                  let retVal = '';

                  if ( elusiveData?.calendars ) {
                      for ( const calendarKey of Object.keys( elusiveData.calendars ) ) {
                          retVal += JSON.stringify( elusiveData.calendars[ calendarKey ].data ?? {} );
                      }
                  }

                  return retVal;
              },
              [
                  elusiveData?.calendars
              ]
          ),
          isConfigLoaded                                          = useMemo(
              () => {
                  return elusiveData?.configLoaded ?? false;
              },
              [
                  elusiveData?.configLoaded ?? false
              ]
          ),
          isDataLoaded                                            = useMemo(
              () => {
                  return elusiveData?.dataLoaded ?? false;
              },
              [
                  elusiveData?.dataLoaded ?? false
              ]
          ),
          isEditable                                              = useMemo(
              () => {
                  return editable && queryType === 'DataStructure';
              },
              [
                  editable,
                  queryType
              ]
          ),
          checkIsDirty                                            = ( dataContextToUse : string, dataStructureToUse : DataStructure, calendarEventsToUse : CalendarEvent[] ) => {
              let retVal = false;

              if ( calendarEventsToUse && dataStructureToUse ) {
                  for ( let calendarEventIndex = 0; calendarEventIndex < calendarEventsToUse.length; calendarEventIndex++ ) {
                      const calendarEvent = calendarEventsToUse[ calendarEventIndex ];

                      if ( calendarEvent.dataDescriptors?.length && calendarEvent.rootDataDescriptor ) {
                          const calendarEventItems = (
                              ( dataUtilities.getData(
                                  calendarEvent.rootDataDescriptor,
                                  dataStructureToUse,
                                  dataContextToUse
                              ) ?? [] ) as ItemData[] )
                              .map( cEI => cEI.object );

                          for ( const calendarEventRootItem of calendarEventItems ) {
                              if ( calendarEventRootItem.isNew() || calendarEventRootItem.isModified() ) {
                                  retVal = true;
                              }

                              else {
                                  const subDataStructure = getDataStructure( calendarEvent.rootDataDescriptor.dataObject.id, dataStructureToUse );

                                  if ( subDataStructure ) {

                                      // get data object by DataDescriptors
                                      for ( const dataDescriptor of calendarEvent.dataDescriptors ) {
                                          if ( dataDescriptor.type !== 'static' && dataDescriptor.type !== 'expression' ) {
                                              const dataObject = dataUtilities.getData(
                                                  dataDescriptor,
                                                  subDataStructure,
                                                  calendarEventRootItem.dataContext,
                                                  calendarEventRootItem.id
                                              )?.[ 0 ].object;

                                              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                              if ( dataObject?.isNew() || dataObject?.isModified() ) {
                                                  retVal = true;
                                                  break;
                                              }
                                          }
                                      }
                                  }
                              }

                              if ( retVal ) {
                                  break;
                              }
                          }
                      }
                  }
              }

              return retVal;
          },
          language                                                = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          dataSchema                                              = useMemo(
              () => {
                  return ( dataStructure?.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;
              },
              [
                  dataStructure
              ]
          ),
          firstDayOfWeek                                          = useMemo(
              () => {
                  return moment.localeData( ( new Intl.NumberFormat() ).resolvedOptions().locale ).firstDayOfWeek() as 0 | 1 | 2 | 3 | 4 | 5 | 6;
              },
              [
                  ( new Intl.NumberFormat() ).resolvedOptions().locale
              ]
          ),
          backgroundEvents                                        = useMemo(
              () => {
                  const retVal : { [ date : string ] : EventCalendar.Event[] } = {};

                  if ( elusiveData?.events ) {
                      for ( const event of elusiveData.events ) {
                          if ( event.extendedProps?.background ) {
                              const eventDate    = moment( event.start ),
                                    eventEndDate = moment( event.end );

                              while ( eventDate <= eventEndDate ) {
                                  const eventDateString = eventDate.tz( 'utc', true ).format( 'YYYY-MM-DD' );

                                  if ( !retVal[ eventDateString ] ) {
                                      retVal[ eventDateString ] = [];
                                  }

                                  retVal[ eventDateString ].push( event );

                                  eventDate.add( 1, 'day' );
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  elusiveData?.events
              ]
          ),
          editableCalendarEvents                                  = useMemo(
              () => {
                  const retVal : EditableCalendarEvent[] = [];

                  if ( isEditable ) {

                      // main calendar events
                      if ( dataStructure && calendarEvents?.length ) {
                          for ( const calendarEvent of calendarEvents ) {
                              if ( calendarEvent.data.isEditable ) {
                                  retVal.push( {
                                      calendarEvent,
                                      dataContext,
                                      dataStructure
                                  } );
                              }
                          }
                      }

                      // sub-calendar events
                      for ( const calendar of calendars ?? [] ) {
                          if ( calendar.editable ) {
                              for ( const calendarEvent of calendar.calendarEvents ) {
                                  if ( calendarEvent.data.isEditable ) {
                                      retVal.push( {
                                          calendarEvent,
                                          dataContext  : `${dataContext}-${calendar.key}`,
                                          dataStructure: calendar.dataStructure
                                      } );
                                  }
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  calendarEvents,
                  calendars,
                  dataContext,
                  dataStructure,
                  isEditable
              ]
          ),
          getValueAndStringValueForDataDescriptor                 = useCallback(
              ( dataDescriptorToUse : DataDescriptor, dataStructureToUse : DataStructure, calendarEventRootItem : Item, rowData : LoadMetaDataRowData ) => {
                  const retVal : { value : any; stringValue : string; } = {
                      value      : undefined,
                      stringValue: ''
                  };

                  if ( dataDescriptorToUse.type !== 'static' && dataDescriptorToUse.type !== 'expression' ) {
                      const dataDescriptorData  = dataUtilities.getData(
                                dataDescriptorToUse,
                                dataStructureToUse,
                                calendarEventRootItem.dataContext,
                                calendarEventRootItem.id
                            )?.[ 0 ],
                            dataDescriptorValue = dataDescriptorData && dataDescriptorData.type !== 'propertySet' && dataDescriptorData.type !== 'referencePropertySet' ?
                                dataUtilities.getValueFromData(
                                    dataDescriptorToUse,
                                    dataDescriptorData
                                )
                                : undefined;

                      if ( dataDescriptorValue !== undefined ) {
                          let value = dataDescriptorValue;

                          retVal.value = value;

                          // enum
                          if ( Array.isArray( value ) ) {
                              retVal.stringValue = value.join( ', ' );
                          }

                          // not empty
                          else if (
                              value !== undefined
                              && value !== null
                              && String( value ).trim() !== ''
                          ) {
                              const propertySet = dataDescriptorData!.type === 'property'
                                        ? ( dataDescriptorData!.object as Property ).propertySet
                                        : dataDescriptorData!.type === 'referenceProperty'
                                            ? ( dataDescriptorData!.object as ReferenceProperty ).referencePropertySet
                                            : undefined,
                                    valueType   = propertySet?.valueType ?? dataUtilities.getDataValueType(
                                        dataDescriptorToUse,
                                        calendarEventRootItem.schema,
                                        dataStructureToUse
                                    );

                              // ValueSet
                              if ( propertySet?.valueSetId ) {
                                  value = String( dataDescriptorValue );
                                  retVal.stringValue = getValueDisplayName( propertySet.valueSetId, value, calendarEventRootItem.schema );
                              }

                              // normal
                              else {
                                  const renderValueParams : any = {
                                      schema,
                                      language : user?.language,
                                      valueUnit: propertySet?.valueUnit,
                                      valuePrecision
                                  };

                                  retVal.stringValue = renderValue( valueType, value, renderValueParams, false, true ) as string;
                              }
                          }
                          else {
                              retVal.stringValue = '';
                          }
                      }
                  }

                  if ( retVal.value === undefined && rowData ) {
                      retVal.value = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptorToUse.alias ] ?? '' ]
                          ?? rowData[ dataDescriptorToUse.alias ];
                  }

                  if ( !retVal.stringValue && retVal.value !== undefined ) {
                      retVal.stringValue = String( retVal.value );
                  }

                  return retVal;
              },
              [
                  elusiveData?.metaData?.rowDataMapping
              ]
          ),
          /**
           * Retrieves the key for the edit form based on the provided calendar event, schema, and item type name.
           *
           * @param {CalendarEvent} calendarEventToUse - The calendar event object that may contain panel configuration IDs.
           * @param {Schema} schemaToUse - The schema object used to determine the form panel properties.
           * @param {string} itemTypeNameToUse - The name of the item type to retrieve panel configuration for.
           * @return {string | undefined} The view key of the edit form panel, or undefined if no configuration is found.
           */
          getEditFormKey                                          = ( calendarEventToUse : CalendarEvent, schemaToUse : Schema, itemTypeNameToUse : string ) => {
              let panelConfig : FormPanelProps | undefined;

              if ( calendarEventToUse.panelConfigIds?.length ) {
                  panelConfig = user?.getPanelConfigFromIdList(
                      calendarEventToUse.panelConfigIds,
                      undefined,
                      'Form'
                  ) as FormPanelProps | undefined;
              }

              if ( !panelConfig ) {
                  panelConfig = user?.getPanelPropsForItemType(
                      schemaToUse,
                      itemTypeNameToUse,
                      undefined,
                      'Form'
                  )?.[ 0 ] as FormPanelProps | undefined;
              }

              return panelConfig?.viewKey;
          },
          /**
           * Builds an event object for a calendar based on the provided calendarEvent definition, data mappings, and formatting options.
           *
           * @param {CalendarEvent} calendarEvent - The configuration object describing event metadata such as time, background, and messages.
           * @param {string} dataContextToUse - The identifier to access global.data with.
           * @param {DataStructure} dataStructureToUse - The data structure object to fetch hierarchical or relational data.
           * @param {string} calendarEventId - The unique identifier for the calendar event.
           * @param {Data} data - The actual event data mapped by data descriptor aliases.
           * @param {StringData} stringData - A mapping of string values for localized or textual event details by data descriptor aliases.
           * @param {string} backgroundColor - The background color for the event display.
           * @param {{ [schemaAndItemTypeName: string]: string }} resolvedEditFormKeys
           * @param {Query[ 'type' ]} calendarQueryType - The query type of the calendar the event belongs to.
           * @param {boolean} calendarIsEditable - Is the calendar the event belongs to editable at all or not.
           * @param {Item} [calendarEventRootItem]
           */
          buildEvent                                              = ( calendarEvent : CalendarEvent, dataContextToUse : string, dataStructureToUse : DataStructure, calendarEventId : string, data : Data, stringData : StringData, backgroundColor : string, resolvedEditFormKeys : ResolvedEditFormKeys, calendarQueryType : Query[ 'type' ], calendarIsEditable : boolean, calendarEventRootItem? : Item ) => {
              const extendedProps : ExtendedProps = {
                  background   : false,
                  calendarEvent,
                  dataContext  : dataContextToUse,
                  dataStructure: dataStructureToUse
              };

              let retVal : EventCalendar.Event | undefined,
                  start : Date | undefined,
                  end : Date | undefined,
                  allDay : boolean     = false,
                  background : boolean = false,
                  title : string | undefined,
                  textColor : string   = getTextColorForBackgroundColor( backgroundColor );

              if ( dataStructure && dataDescriptors ) {
                  try {
                      // process from
                      if ( calendarEvent.fromDataDescriptor ) {
                          if ( Object.prototype.toString.call( data[ calendarEvent.fromDataDescriptor.alias ] ) !== '[object Date]' ) {
                              start = convertToDate( data[ calendarEvent.fromDataDescriptor.alias ] ) as Date | undefined;
                          }
                          else {
                              start = data[ calendarEvent.fromDataDescriptor.alias ];
                          }
                      }

                      // process to
                      if ( calendarEvent.toDataDescriptor ) {
                          if ( Object.prototype.toString.call( data[ calendarEvent.toDataDescriptor.alias ] ) !== '[object Date]' ) {
                              end = convertToDate( data[ calendarEvent.toDataDescriptor.alias ] ) as Date | undefined;
                          }
                          else {
                              end = data[ calendarEvent.toDataDescriptor.alias ];
                          }
                      }

                      // process allDay
                      if ( calendarEvent.allDayDataDescriptor ) {
                          allDay = convertToBoolean( data[ calendarEvent.allDayDataDescriptor.alias ] ?? '0' ) ?? false;
                      }

                      // process background
                      if ( calendarEvent.backgroundDataDescriptor ) {
                          background = convertToBoolean( data[ calendarEvent.backgroundDataDescriptor.alias ] ?? '0' ) ?? false;
                      }
                  }
                  catch ( e ) {
                      console.log( e );
                  }

                  // process title
                  if ( calendarEvent.messages?.title ) {
                      if ( calendarQueryType === 'DataStructure' ) {
                          if ( typeof calendarEvent.messages.title !== 'string' ) {
                              const messageStringData : { [ labeldAttributePath : string ] : string } = {};

                              for ( const dataDescriptor of dataDescriptors ) {
                                  for ( const dataObjectLabel of Object.keys( calendarEvent.messages.title.dataObjects ) ) {
                                      const dataObject = calendarEvent.messages.title.dataObjects[ dataObjectLabel ];

                                      if ( dataObject.origId === dataDescriptor.dataObject?.origId ) {
                                          messageStringData[ `${dataObjectLabel}.${dataDescriptor.attributePath}` ] = stringData[ dataDescriptor.alias ];
                                      }
                                  }
                              }

                              title = __( calendarEvent.messages.title.key, messageStringData );
                          }
                          else if ( calendarEventRootItem ) {
                              title = messageUtilities.resolveMessageForDataStructure( calendarEvent.messages.title, dataStructure, dataContext, calendarEventRootItem );
                          }
                          else {
                              title = __( calendarEvent.messages.title );
                          }
                      }
                      else if ( typeof calendarEvent.messages.title === 'string' ) {
                          title = __( calendarEvent.messages.title, stringData );
                      }
                  }

                  // process popup title
                  if ( calendarEvent.messages?.popupTitle ) {
                      if ( calendarQueryType === 'DataStructure' ) {
                          if ( typeof calendarEvent.messages.popupTitle !== 'string' ) {
                              const messageStringData : { [ labeldAttributePath : string ] : string } = {};

                              for ( const dataDescriptor of dataDescriptors ) {
                                  for ( const dataObjectLabel of Object.keys( calendarEvent.messages.popupTitle.dataObjects ) ) {
                                      const dataObject = calendarEvent.messages.popupTitle.dataObjects[ dataObjectLabel ];

                                      if ( dataObject.origId === dataDescriptor.dataObject?.origId ) {
                                          messageStringData[ `${dataObjectLabel}.${dataDescriptor.attributePath}` ] = stringData[ dataDescriptor.alias ];
                                      }
                                  }
                              }

                              extendedProps.editFormTitle = __( calendarEvent.messages.popupTitle.key, messageStringData );
                          }
                          else if ( calendarEventRootItem ) {
                              extendedProps.editFormTitle = messageUtilities.resolveMessageForDataStructure( calendarEvent.messages.popupTitle, dataStructure, dataContext, calendarEventRootItem );
                          }
                          else {
                              extendedProps.editFormTitle = __( calendarEvent.messages.popupTitle );
                          }
                      }
                      else if ( typeof calendarEvent.messages.popupTitle === 'string' ) {
                          extendedProps.editFormTitle = __( calendarEvent.messages.popupTitle, stringData );
                      }
                  }

                  // process style
                  if ( calendarEvent.styles?.style?.length ) {
                      const style = resolveStyle( calendarEvent.styles.style, data );

                      if ( style[ 'backgroundColor' ] ) {
                          backgroundColor = style[ 'backgroundColor' ];
                      }

                      if ( style[ 'color' ] ) {
                          textColor = style[ 'color' ];
                      }
                      else {
                          textColor = getTextColorForBackgroundColor( backgroundColor );
                      }
                  }

                  // get extendedProps
                  if ( calendarEventRootItem ) {
                      if ( calendarEventRootItem.isModified() || calendarEventRootItem.isNew() ) {
                          extendedProps.isDirty = true;
                      }

                      extendedProps.rootItemId = calendarEventRootItem.id;
                      extendedProps.rootItemSchema = calendarEventRootItem.schema;
                      extendedProps.rootItemTypeName = calendarEventRootItem.itemType.name;

                      const resolvedEditFormKey = `${extendedProps.rootItemSchema}:${extendedProps.rootItemTypeName}`;

                      if ( !Object.keys( resolvedEditFormKeys ).includes( resolvedEditFormKey ) ) {
                          const eFK = getEditFormKey( calendarEvent, extendedProps.rootItemSchema, extendedProps.rootItemTypeName );

                          if ( eFK ) {
                              resolvedEditFormKeys[ resolvedEditFormKey ] = eFK;
                          }
                      }

                      extendedProps.editFormKey = resolvedEditFormKeys[ resolvedEditFormKey ];
                  }
                  else if ( calendarEvent.openDetailViewConfig ) {
                      if ( typeof data[ calendarEvent.openDetailViewConfig.queryFieldName ] === 'number' ) {
                          extendedProps.rootItemId = data[ calendarEvent.openDetailViewConfig.queryFieldName ];
                      }
                      else {
                          extendedProps.rootItemKey = data[ calendarEvent.openDetailViewConfig.queryFieldName ];
                      }

                      extendedProps.rootItemTypeName = data[ calendarEvent.openDetailViewConfig.itemTypeNameOrQueryFieldName ];
                  }

                  extendedProps.background = background;

                  if ( start && end ) {
                      retVal = {
                          id              : calendarEventId,
                          allDay          : allDay || background,
                          start,
                          end,
                          title           : title ?? '',
                          backgroundColor,
                          textColor,
                          extendedProps,
                          editable        : isEditable && !background && calendarIsEditable && calendarEvent.data.isEditable,
                          startEditable   : isEditable && !background && calendarIsEditable && calendarEvent.data.isEditable,
                          durationEditable: isEditable && !background && calendarIsEditable && calendarEvent.data.isEditable,

                          resourceIds: [],
                          classNames : [],
                          display    : 'auto',
                          styles     : []
                      };
                  }
              }

              return retVal;
          },
          /**
           * Builds a list of calendar events based on the provided schema, data context, data structure, and descriptors.
           *
           * @param {Schema} schemaToUse - The schema object used to retrieve and process value mappings and display names.
           * @param {string} dataContextToUse - The identifier to access global.data with.
           * @param {DataStructure} dataStructureToUse - The data structure object to fetch hierarchical or relational data.
           * @param {DataDescriptor[]} dataDescriptorsToUse - An array of data descriptors that define data mappings for extracting values.
           * @param {CalendarEvent[]} calendarEventsToUse - An array of calendar events, each containing metadata about the event and its data requirements.
           * @param {Query<'type'>} queryTypeToUse - The query type designation, which determines whether a structure or a data view query is performed.
           * @param {boolean} isEditableToUse - Is the calendar the event belongs to editable at all or not.
           * @param {LoadMetaDataRowData[]} [rowDataToUse] - Optional. An array of row data objects containing preloaded data for the calendar events.
           * @param {LoadMetaDataRowDataMapping} [rowDataMappingToUse] - Optional. A mapping object linking data descriptor aliases to respective properties in the row data.
           * @return {EventCalendar.Event[]} An array of constructed calendar events, enriched with data and string representations, ready for rendering in a calendar view.
           */
          buildEvents                                             = ( schemaToUse : Schema, dataContextToUse : string, dataStructureToUse : DataStructure, dataDescriptorsToUse : DataDescriptor[], calendarEventsToUse : CalendarEvent[], queryTypeToUse : Query[ 'type' ], isEditableToUse : boolean, rowDataToUse? : LoadMetaDataRowData[], rowDataMappingToUse? : LoadMetaDataRowDataMapping ) => {
              const innerRetVal : EventCalendar.Event[] = [];

              for ( let calendarEventIndex = 0; calendarEventIndex < calendarEventsToUse.length; calendarEventIndex++ ) {
                  const calendarEvent                                                           = calendarEventsToUse[ calendarEventIndex ],
                        resolvedEditFormKeys : { [ schemaAndItemTypeName : string ] : string; } = {},
                        currentStartDate                                                        = moment( viewStartDate, 'YYYY-MM-DD' ).tz( 'utc', true ).toDate(),
                        currentEndDate                                                          = moment( viewEndDate, 'YYYY-MM-DD' ).tz( 'utc', true ).toDate();

                  if ( calendarEvent.dataDescriptors?.length && calendarEvent.fromDataDescriptor && calendarEvent.toDataDescriptor ) {
                      if ( queryTypeToUse === 'DataStructure' && calendarEvent.rootDataDescriptor ) {
                          const calendarEventItems = (
                              ( dataUtilities.getData(
                                  calendarEvent.rootDataDescriptor,
                                  dataStructureToUse,
                                  dataContextToUse
                              ) ?? [] ) as ItemData[] )
                              .map( cEI => cEI.object );

                          for ( const calendarEventRootItem of calendarEventItems ) {
                              const subDataStructure        = getDataStructure( calendarEvent.rootDataDescriptor.dataObject.id, dataStructureToUse ),
                                    rowData                 = ( rowDataToUse?.find( row => row[ calendarEvent.rootDataDescriptor!.alias ] === calendarEventRootItem.id ) ?? [] ) as LoadMetaDataRowData,
                                    data : Data             = {},
                                    stringData : StringData = {};

                              // get data from loaded data
                              if ( subDataStructure ) {

                                  // get from and to data and stringData
                                  const { value: fromValue, stringValue: fromStringValue } = getValueAndStringValueForDataDescriptor( calendarEvent.fromDataDescriptor, subDataStructure, calendarEventRootItem, rowData ),
                                        { value: toValue, stringValue: toStringValue }     = getValueAndStringValueForDataDescriptor( calendarEvent.toDataDescriptor, subDataStructure, calendarEventRootItem, rowData );

                                  // skip if event not within current shown interval
                                  if ( fromValue > currentEndDate || toValue < currentStartDate ) {
                                      continue;
                                  }

                                  data[ calendarEvent.fromDataDescriptor.alias ] = fromValue;
                                  stringData[ calendarEvent.fromDataDescriptor.alias ] = fromStringValue;
                                  data[ calendarEvent.toDataDescriptor.alias ] = toValue;
                                  stringData[ calendarEvent.toDataDescriptor.alias ] = toStringValue;

                                  // get data and stringData by DataDescriptors
                                  for ( const dataDescriptor of dataDescriptorsToUse ) {
                                      if ( dataDescriptor !== calendarEvent.fromDataDescriptor && dataDescriptor !== calendarEvent.toDataDescriptor ) {
                                          const { value, stringValue } = getValueAndStringValueForDataDescriptor( dataDescriptor, subDataStructure, calendarEventRootItem, rowData );

                                          data[ dataDescriptor.alias ] = value;
                                          stringData[ dataDescriptor.alias ] = stringValue;
                                      }
                                  }
                              }

                              // get missing data and stringData from rowData
                              for ( const key of Object.keys( rowData ) ) {
                                  if ( data[ key ] === undefined ) {
                                      data[ key ] = rowData[ key ];
                                  }
                                  if ( stringData[ key ] === undefined ) {
                                      stringData[ key ] = String( rowData[ key ] );
                                  }
                              }

                              const entry = buildEvent( calendarEvent, dataContextToUse, dataStructureToUse, String( calendarEventIndex ), data, stringData, getColorFromColorPalette( calendarEventIndex ), resolvedEditFormKeys, queryTypeToUse, isEditableToUse, calendarEventRootItem );

                              if ( entry ) {
                                  innerRetVal.push( entry );
                              }
                          }
                      }
                      else if ( rowDataToUse ) {
                          for ( const rowDataRaw of rowDataToUse ) {
                              const rowData                 = rowDataRaw as LoadMetaDataRowData,
                                    data : Data             = {},
                                    stringData : StringData = {};

                              let fromValue,
                                  toValue;

                              // get data and stringData by DataDescriptors
                              for ( const dataDescriptor of dataDescriptorsToUse ) {
                                  if ( queryTypeToUse === 'DataStructure' ) {
                                      const value = getValueForDataDescriptorFromRowData(
                                          dataContextToUse,
                                          dataDescriptor,
                                          rowData,
                                          dataStructureToUse
                                      );

                                      if ( value ) {
                                          data[ dataDescriptor.alias ] = value.value;

                                          // enum
                                          if ( Array.isArray( value.value ) ) {
                                              stringData[ dataDescriptor.alias ] = value.value.join( ', ' );
                                          }

                                          // not empty
                                          else if ( value.value !== undefined && value.value !== null && String( value.value ).trim() !== '' ) {
                                              // ValueSet
                                              if ( value.valueSetId ) {
                                                  stringData[ dataDescriptor.alias ] = getValueDisplayName( value.valueSetId, value.value, schemaToUse );
                                              }

                                              // normal
                                              else {
                                                  const renderValueParams : any = {
                                                      schema   : schemaToUse,
                                                      language : user?.language,
                                                      valueUnit: value.valueUnit,
                                                      valuePrecision
                                                  };

                                                  stringData[ dataDescriptor.alias ] = renderValue( value.valueType, value.value, renderValueParams, false, true ) as string;
                                              }
                                          }
                                          else {
                                              stringData[ dataDescriptor.alias ] = '';
                                          }
                                      }
                                  }

                                  if ( !data[ dataDescriptor.alias ] ) {
                                      data[ dataDescriptor.alias ] = rowData[ rowDataMappingToUse?.[ dataDescriptor.alias ] ?? '' ]
                                          ?? rowData[ dataDescriptor.alias ];

                                      stringData[ dataDescriptor.alias ] = String( data[ dataDescriptor.alias ] );
                                  }
                              }

                              // get missing data and stringData from rowData
                              for ( const key of Object.keys( rowData ) ) {
                                  if ( data[ key ] === undefined ) {
                                      data[ key ] = rowData[ key ];
                                  }
                                  if ( stringData[ key ] === undefined ) {
                                      stringData[ key ] = String( rowData[ key ] );
                                  }
                              }

                              // skip if event not within current shown interval
                              if ( Object.prototype.toString.call( data[ calendarEvent.fromDataDescriptor.alias ] ) !== '[object Date]' ) {
                                  fromValue = convertToDate( data[ calendarEvent.fromDataDescriptor.alias ] ) as Date | undefined;
                              }
                              else {
                                  fromValue = data[ calendarEvent.fromDataDescriptor.alias ];
                              }
                              if ( Object.prototype.toString.call( data[ calendarEvent.toDataDescriptor.alias ] ) !== '[object Date]' ) {
                                  toValue = convertToDate( data[ calendarEvent.toDataDescriptor.alias ] ) as Date | undefined;
                              }
                              else {
                                  toValue = data[ calendarEvent.toDataDescriptor.alias ];
                              }
                              if ( fromValue > currentEndDate || toValue < currentStartDate ) {
                                  continue;
                              }

                              const entry = buildEvent( calendarEvent, dataContextToUse, dataStructureToUse, String( calendarEventIndex ), data, stringData, getColorFromColorPalette( calendarEventIndex ), resolvedEditFormKeys, queryTypeToUse, isEditableToUse );

                              if ( entry ) {
                                  innerRetVal.push( entry );
                              }
                          }
                      }
                  }
              }

              return innerRetVal;
          },
          createNewEvent                                          = useCallback(
              ( calendarEvent : CalendarEvent, dataStructureToUse : DataStructure, dataContextToUse : string, allDay : boolean, start : Date, end? : Date ) => {
                  if ( dataStructureToUse && calendarEvent.rootDataDescriptor?.dataObject.item.objectType ) {
                      const newItemSchema = ( calendarEvent.rootDataDescriptor.dataObject.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema,
                            newItemType   = metaModel[ newItemSchema ].getItemType( calendarEvent.rootDataDescriptor?.dataObject.item.objectType );

                      if ( newItemType ) {
                          const activeStatusId                                = metaModel[ newItemSchema ].getStatus( 'active' )!.id,
                                newItemAttributes : ItemAttributesForCreation = {
                                    statusId  : activeStatusId,
                                    itemTypeId: newItemType.id
                                };

                          // set start
                          if (
                              calendarEvent.fromDataDescriptor
                              && (
                                  calendarEvent.fromDataDescriptor.type === 'item'
                                  || calendarEvent.fromDataDescriptor.type === 'property'
                              )
                              && calendarEvent.rootDataDescriptor.dataObject.origId === calendarEvent.fromDataDescriptor.dataObject.origId
                          ) {
                              const valueType = dataUtilities.getDataValueType(
                                  calendarEvent.fromDataDescriptor,
                                  newItemSchema,
                                  dataStructureToUse
                              );

                              if ( isPropertyValueType( valueType ) ) {
                                  const valueToSet = convertValueToString( start, valueType );

                                  let attribute,
                                      metaModelData : MetaModelData | undefined;

                                  switch ( calendarEvent.fromDataDescriptor.type ) {
                                      case 'item':
                                          attribute = calendarEvent.fromDataDescriptor.attribute;
                                          if ( isItemOnlyNotRequiredAttributeForCreation( attribute ) ) {
                                              newItemAttributes[ attribute ] = valueToSet;
                                          }
                                          break;

                                      case 'property':
                                          if ( calendarEvent.fromDataDescriptor.attribute === 'value' ) {
                                              metaModelData = dataUtilities.getMetaModelData(
                                                  calendarEvent.fromDataDescriptor,
                                                  dataStructureToUse,
                                                  newItemSchema
                                              );

                                              if ( metaModelData?.length === 1 && metaModelData[ 0 ].type === 'propertySet' ) {
                                                  if ( !newItemAttributes.properties ) {
                                                      newItemAttributes.properties = [];
                                                  }

                                                  newItemAttributes.properties.push( {
                                                      propertySetId: metaModelData[ 0 ].object.id,
                                                      statusId     : activeStatusId,
                                                      value        : valueToSet
                                                  } );
                                              }
                                          }
                                          break;
                                  }
                              }
                          }

                          // set end if
                          if (
                              end
                              && calendarEvent.toDataDescriptor
                              && (
                                  calendarEvent.toDataDescriptor.type === 'item'
                                  || calendarEvent.toDataDescriptor.type === 'property'
                              )
                              && calendarEvent.rootDataDescriptor.dataObject.origId === calendarEvent.toDataDescriptor.dataObject.origId
                          ) {
                              const valueType = dataUtilities.getDataValueType(
                                  calendarEvent.toDataDescriptor,
                                  newItemSchema,
                                  dataStructureToUse
                              );

                              if ( isPropertyValueType( valueType ) ) {
                                  const valueToSet = convertValueToString( end, valueType );

                                  let attribute,
                                      metaModelData : MetaModelData | undefined;

                                  switch ( calendarEvent.toDataDescriptor.type ) {
                                      case 'item':
                                          attribute = calendarEvent.toDataDescriptor.attribute;
                                          if ( isItemOnlyNotRequiredAttributeForCreation( attribute ) ) {
                                              newItemAttributes[ attribute ] = valueToSet;
                                          }
                                          break;

                                      case 'property':
                                          if ( calendarEvent.toDataDescriptor.attribute === 'value' ) {
                                              metaModelData = dataUtilities.getMetaModelData(
                                                  calendarEvent.toDataDescriptor,
                                                  dataStructureToUse,
                                                  newItemSchema
                                              );

                                              if ( metaModelData?.length === 1 && metaModelData[ 0 ].type === 'propertySet' ) {
                                                  if ( !newItemAttributes.properties ) {
                                                      newItemAttributes.properties = [];
                                                  }

                                                  newItemAttributes.properties.push( {
                                                      propertySetId: metaModelData[ 0 ].object.id,
                                                      statusId     : activeStatusId,
                                                      value        : valueToSet
                                                  } );
                                              }
                                          }
                                          break;
                                  }
                              }
                          }

                          // set allDay
                          if (
                              calendarEvent.allDayDataDescriptor
                              && (
                                  calendarEvent.allDayDataDescriptor.type === 'item'
                                  || calendarEvent.allDayDataDescriptor.type === 'property'
                              )
                              && calendarEvent.rootDataDescriptor.dataObject.origId === calendarEvent.allDayDataDescriptor.dataObject.origId
                          ) {
                              const valueType = dataUtilities.getDataValueType(
                                  calendarEvent.allDayDataDescriptor,
                                  newItemSchema,
                                  dataStructureToUse
                              );

                              if ( isPropertyValueType( valueType ) ) {
                                  const valueToSet = convertValueToString( allDay, valueType );

                                  let attribute,
                                      metaModelData : MetaModelData | undefined;

                                  switch ( calendarEvent.allDayDataDescriptor.type ) {
                                      case 'item':
                                          attribute = calendarEvent.allDayDataDescriptor.attribute;
                                          if ( isItemOnlyNotRequiredAttributeForCreation( attribute ) ) {
                                              newItemAttributes[ attribute ] = valueToSet;
                                          }
                                          break;

                                      case 'property':
                                          if ( calendarEvent.allDayDataDescriptor.attribute === 'value' ) {
                                              metaModelData = dataUtilities.getMetaModelData(
                                                  calendarEvent.allDayDataDescriptor,
                                                  dataStructureToUse,
                                                  newItemSchema
                                              );

                                              if ( metaModelData?.length === 1 && metaModelData[ 0 ].type === 'propertySet' ) {
                                                  if ( !newItemAttributes.properties ) {
                                                      newItemAttributes.properties = [];
                                                  }

                                                  newItemAttributes.properties.push( {
                                                      propertySetId: metaModelData[ 0 ].object.id,
                                                      statusId     : activeStatusId,
                                                      value        : valueToSet
                                                  } );
                                              }
                                          }
                                          break;
                                  }
                              }
                          }

                          const newItem        = new Item(
                                    newItemAttributes,
                                    editFormDataContext,
                                    newItemSchema
                                ),
                                newEditFormKey = getEditFormKey(
                                    calendarEvent,
                                    newItemSchema,
                                    newItemType.name
                                );

                          if ( newItem && newEditFormKey ) {
                              hideEditItemDialog();

                              // clone dataContextToUse
                              for ( const item of Object.values( global.data[ dataContextToUse ].items ) ) {
                                  item.clone( undefined, undefined, editFormDataContext );
                              }
                              for ( const reference of Object.values( global.data[ dataContextToUse ].references ) ) {
                                  reference.clone( undefined, undefined, editFormDataContext );
                              }
                              global.data[ editFormDataContext ].items[ newItem.id ] = newItem;

                              editItem.current = newItem;
                              editItemDataContext.current = dataContextToUse;

                              setShowEditItemDialog( true );

                              setEditFormVariables( {
                                  allDay,
                                  start,
                                  end
                              } );
                              setEditFormKey( newEditFormKey );

                              // process popup title (no replacements available here)
                              setEditFormTitle(
                                  calendarEvent.messages?.popupTitle
                                      ? typeof calendarEvent.messages?.popupTitle === 'string'
                                          ? __( calendarEvent.messages.popupTitle )
                                          : __( calendarEvent.messages.popupTitle.key )
                                      : undefined
                              );
                          }
                      }
                  }
              },
              [
                  editFormDataContext
              ]
          ),
          events                                                  = useMemo(
              () => {
                  let retVal : EventCalendar.Event[] = [];

                  if ( dataStructure && dataDescriptors && calendarEvents ) {
                      retVal = retVal.concat( buildEvents(
                          schema,
                          dataContext,
                          dataStructure,
                          dataDescriptors,
                          calendarEvents,
                          queryType,
                          isEditable,
                          elusiveData?.metaData?.rowData,
                          elusiveData?.metaData?.rowDataMapping
                      ) );

                      for ( const calendar of calendars ?? [] ) {
                          retVal = retVal.concat( buildEvents(
                              calendar.schema,
                              `${dataContext}-${calendar.key}`,
                              calendar.dataStructure,
                              calendar.dataDescriptors,
                              calendar.calendarEvents,
                              calendar.queryType,
                              calendar.editable,
                              elusiveData?.calendars?.[ calendar.key ]?.metaData?.rowData,
                              elusiveData?.calendars?.[ calendar.key ]?.metaData?.rowDataMapping
                          ) );
                      }
                  }

                  return retVal;
              },
              [
                  calendarsDataVersions,
                  calendarEvents,
                  calendars,
                  dataContext,
                  dataDescriptors,
                  dataStructure,
                  elusiveData?.metaData?.rowData,
                  elusiveData?.metaData?.rowDataMapping,
                  globalDataVersions,
                  queryType,
                  user,
                  viewEndDate,
                  viewStartDate
              ]
          ),
          loadConfig                                              = useCallback(
              async () => {
                  isLoading.current = true;

                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  const params : LoadConfigParameters = {
                            viewKey
                        },
                        response                      = await xhr.post(
                            `${BASE_URL}/api/calendar/loadConfig`,
                            params,
                            undefined,
                            uid,
                            configRequestUid
                        );

                  if ( response?.data ) {
                      const result = response.data as LoadConfigResult,
                            config = result.data;

                      let newSelectedView = selectedView,
                          newStartDate    = startDate;

                      if ( !selectedView && config.selectedView ) {
                          newSelectedView = config.selectedView;

                          if ( setFormPersistentSubPanelProps ) {
                              setFormPersistentSubPanelProps( 'selectedView', newSelectedView );
                          }
                      }

                      if ( newSelectedView !== selectedView ) {
                          newStartDate = moment().tz( 'utc', true ).format( 'YYYY-MM-DD' );

                          if ( setFormPersistentSubPanelProps ) {
                              setFormPersistentSubPanelProps( 'startDate', newStartDate );
                          }
                      }

                      if ( setFormPersistentSubPanelProps ) {
                          setFormPersistentSubPanelProps( 'viewStartDate', undefined );
                          setFormPersistentSubPanelProps( 'viewEndDate', undefined );
                      }

                      dispatch( setParams( uid, {
                          calendars             : config.calendars,
                          calendarEvents        : config.calendarEvents,
                          configHash            : config.configHash,
                          dataDescriptors       : config.dataDescriptors,
                          dataStructure         : config.dataStructure,
                          editable              : config.editable,
                          queryType             : config.queryType,
                          schema                : config.schema,
                          selectedView          : newSelectedView,
                          startDate             : newStartDate,
                          viewStartDate         : undefined,
                          viewEndDate           : undefined,
                          flexibleSlotTimeLimits: config.flexibleSlotTimeLimits ?? flexibleSlotTimeLimits,
                          slotMaxTime           : config.slotMaxTime ?? slotMaxTime,
                          slotMinTime           : config.slotMinTime ?? slotMinTime
                      } ) );

                      onConfigLoad?.( config );
                  }

                  dispatch( setElusiveData( uid, { configLoaded: true } ) );

                  dispatch( endRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  isLoading.current = false;
              },
              [
                  configRequestUid,
                  flexibleSlotTimeLimits,
                  selectedView,
                  setFormPersistentSubPanelProps,
                  slotMaxTime,
                  slotMinTime,
                  startDate,
                  uid,
                  viewKey
              ]
          ),
          loadIntervalData                                        = useCallback(
              async ( start : string, end : string, newElusiveData : ElusiveData ) : Promise<ElusiveData> => {
                  const params : LoadDataParameters = {
                      viewKey,
                      configHash,
                      start,
                      end,
                      filters,
                      parameters: {
                          ...additionalQueryParameters
                      }
                  };

                  await dispatch(
                      loadData(
                          'calendar/loadData',
                          dataContext,
                          `${uid}_load`,
                          dataRequestUid,
                          dataSchema,
                          {
                              params,
                              mergeMode        : 'keepLoaded',
                              extraMetaDataKeys: [ 'rowDataMapping' ]
                          },
                          false,
                          ( responseData ) => {
                              // metaData
                              newElusiveData = {
                                  ...newElusiveData,
                                  metaData: {
                                      ...newElusiveData.metaData,

                                      // set rowDataMapping
                                      rowDataMapping: responseData.metaData.rowDataMapping,

                                      // merge rowData
                                      rowData: _.uniqBy( ( [] as LoadMetaDataRowData[] ).concat( newElusiveData.metaData?.rowData ?? [] ).concat( responseData.metaData.rowData ?? [] ), JSON.stringify )
                                  }
                              };

                              // add loaded interval and consolidate intervals
                              newElusiveData.loadedDataIntervals = ( [] as LoadedDataIntervals ).concat( newElusiveData.loadedDataIntervals ?? [] );
                              // add loaded interval
                              newElusiveData.loadedDataIntervals.push( {
                                  start,
                                  end
                              } );
                              // consolidate loadedDataIntervals
                              // 1. sort by start asc
                              newElusiveData.loadedDataIntervals.sort( ( v1, v2 ) => {
                                  if ( v1.start > v2.start ) {
                                      return 1;
                                  }
                                  else if ( v1.start < v2.start ) {
                                      return -1;
                                  }
                                  else {
                                      return 0;
                                  }
                              } );
                              // 2. reduce by joining overlapping intervals
                              for ( let i = newElusiveData.loadedDataIntervals.length - 1; i >= 1; i-- ) {
                                  if ( newElusiveData.loadedDataIntervals[ i ].start <= newElusiveData.loadedDataIntervals[ i - 1 ].end ) {
                                      if ( newElusiveData.loadedDataIntervals[ i ].end > newElusiveData.loadedDataIntervals[ i - 1 ].end ) {
                                          newElusiveData.loadedDataIntervals[ i - 1 ].end = newElusiveData.loadedDataIntervals[ i ].end;
                                      }
                                      newElusiveData.loadedDataIntervals.splice( i, 1 );
                                  }
                              }

                              // process calendars
                              newElusiveData.calendars = { ...newElusiveData.calendars };

                              for ( const calendar of calendars ?? [] ) {
                                  if ( responseData.data.calendars[ calendar.key ] ) {
                                      const calendarDataContext = `${dataContext}-${calendar.key}`,
                                            data                = processData( responseData.data.calendars[ calendar.key ].data, calendarDataContext, calendar.schema, 'keepLoaded' );

                                      newElusiveData.calendars[ calendar.key ] = {
                                          data    : {
                                              items     : {
                                                  ...newElusiveData.calendars?.[ calendar.key ]?.data?.items
                                              },
                                              references: {
                                                  ...newElusiveData.calendars?.[ calendar.key ]?.data?.references
                                              }
                                          },
                                          metaData: {
                                              // set rowDataMapping
                                              rowDataMapping: responseData.data.calendars[ calendar.key ].metaData.rowDataMapping,

                                              // merge rowData
                                              rowData: _.uniqBy( ( [] as LoadMetaDataRowData[] ).concat( newElusiveData.calendars?.[ calendar.key ]?.metaData?.rowData ?? [] ).concat( responseData.data.calendars[ calendar.key ].metaData.rowData ?? [] ), JSON.stringify )
                                          }
                                      };

                                      for ( const itemId of data.items ) {
                                          newElusiveData.calendars[ calendar.key ].data!.items![ itemId ] = ( newElusiveData.calendars[ calendar.key ]?.data?.items?.[ itemId ] ?? 0 ) + 1;
                                      }

                                      for ( const referenceId of data.references ) {
                                          newElusiveData.calendars[ calendar.key ].data!.references![ referenceId ] = ( newElusiveData.calendars[ calendar.key ]?.data?.references?.[ referenceId ] ?? 0 ) + 1;
                                      }
                                  }
                              }
                          }
                      )
                  );

                  // set elusive data
                  dispatch( setElusiveData(
                      uid,
                      {
                          calendars          : newElusiveData.calendars,
                          metaData           : newElusiveData.metaData,
                          loadedDataIntervals: newElusiveData.loadedDataIntervals
                      }
                  ) );

                  return newElusiveData;
              },
              [
                  additionalQueryParameters,
                  calendars,
                  configHash,
                  dataContext,
                  dataSchema,
                  uid,
                  viewKey
              ]
          ),
          load                                                    = useCallback(
              async () => {
                  if ( !isLoading.current ) {
                      isLoading.current = true;

                      dispatch( startRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      if ( viewStartDate && viewEndDate ) {
                          const viewStart = moment( viewStartDate, 'YYYY-MM-DD' ).tz( 'utc', true ),
                                viewEnd   = moment( viewEndDate, 'YYYY-MM-DD' ).tz( 'utc', true ),
                                interval  = moment.duration( viewEnd.diff( viewStart ) );

                          let newElusiveData = { ...elusiveData };

                          newElusiveData.loadedDataIntervals = ( [] as LoadedDataIntervals ).concat( newElusiveData.loadedDataIntervals ?? [] );

                          // load data for interval currently displayed if not loaded
                          if ( !newElusiveData.loadedDataIntervals.find( lDI => lDI.start <= viewStartDate && lDI.end >= viewEndDate ) ) {
                              newElusiveData = await loadIntervalData( viewStartDate, viewEndDate, newElusiveData );
                          }

                          onDataLoad?.();

                          newElusiveData = {
                              ...newElusiveData,
                              dataLoaded: true
                          };

                          dispatch( setElusiveData( uid, { dataLoaded: true } ) );

                          const fetchPreviousAndNextInterval = async () => {
                              let start : string,
                                  end : string;

                              // prefetch previous interval if not loaded
                              start = viewStart.subtract( interval ).format( 'YYYY-MM-DD' );
                              end = viewStartDate;
                              if ( !newElusiveData.loadedDataIntervals!.find( lDI => lDI.start <= start && lDI.end >= end ) ) {
                                  newElusiveData = await loadIntervalData( start, end, newElusiveData );
                              }

                              // prefetch next interval if not loaded
                              start = viewEndDate;
                              end = viewEnd.add( interval ).format( 'YYYY-MM-DD' );
                              if ( !newElusiveData.loadedDataIntervals!.find( lDI => lDI.start <= start && lDI.end >= end ) ) {
                                  await loadIntervalData( start, end, newElusiveData );
                              }
                          };

                          void fetchPreviousAndNextInterval();
                      }

                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;
                  }
              },
              [
                  elusiveData,
                  uid,
                  viewEndDate,
                  viewStartDate
              ]
          ),
          updateDataDescriptorData                                = useCallback(
              ( rootDataObjectId : string, rootItemId : number, dataDescriptor : DataDescriptor, dataContextToUse : string, dataStructureToUse : DataStructure, value : any ) => {
                  if ( dataStructureToUse && dataDescriptor.type !== 'static' && dataDescriptor.type !== 'expression' ) {
                      const calendarEventRootItem = global.data[ dataContextToUse ]?.items?.[ rootItemId ];

                      if ( calendarEventRootItem ) {
                          const subDataStructure   = getDataStructure( rootDataObjectId, dataStructureToUse ),
                                dataDescriptorData = subDataStructure
                                    ? dataUtilities.getData(
                                        dataDescriptor,
                                        subDataStructure,
                                        dataContextToUse,
                                        calendarEventRootItem.id
                                    )?.[ 0 ]
                                    : undefined,
                                propertySet        = dataDescriptorData?.type === 'property'
                                    ? ( dataDescriptorData.object as Property ).propertySet
                                    : dataDescriptorData?.type === 'referenceProperty'
                                        ? ( dataDescriptorData.object as ReferenceProperty ).referencePropertySet
                                        : undefined,
                                valueType          = propertySet?.valueType ?? dataUtilities.getDataValueType(
                                    dataDescriptor,
                                    calendarEventRootItem.schema,
                                    subDataStructure
                                );

                          if ( dataDescriptorData && isPropertyValueType( valueType ) ) {
                              const valueToSet = convertValueToString( value, valueType );

                              switch ( dataDescriptorData.type ) {
                                  case 'item':
                                      dispatch( updateItem(
                                          dataContextToUse,
                                          dataDescriptorData.object.id,
                                          {
                                              [ dataDescriptor.attribute ]: valueToSet
                                          },
                                          additionalElusiveDataContexts
                                      ) );
                                      break;

                                  case 'property':
                                      if ( propertySet ) {
                                          dispatch( updateItem(
                                              dataContextToUse,
                                              dataDescriptorData.object.itemId,
                                              {
                                                  properties: {
                                                      [ String( propertySet.propertyTypeId ) ]: {
                                                          [ dataDescriptor.attribute ]: valueToSet
                                                      }
                                                  }
                                              },
                                              additionalElusiveDataContexts
                                          ) );
                                      }
                                      break;

                                  case 'reference':
                                      dispatch( updateReference(
                                          dataContextToUse,
                                          dataDescriptorData.object.id,
                                          {
                                              [ dataDescriptor.attribute ]: valueToSet
                                          },
                                          additionalElusiveDataContexts
                                      ) );

                                      break;

                                  case 'referenceProperty':
                                      if ( propertySet ) {
                                          dispatch( updateReference(
                                              dataContextToUse,
                                              dataDescriptorData.object.referenceId,
                                              {
                                                  properties: {
                                                      [ String( propertySet.propertyTypeId ) ]: {
                                                          [ dataDescriptor.attribute ]: valueToSet
                                                      }
                                                  }
                                              },
                                              additionalElusiveDataContexts
                                          ) );
                                      }
                                      break;
                              }
                          }
                      }
                  }
              },
              [
                  additionalElusiveDataContexts
              ]
          ),
          getUpdateDataWithRemovedNewProperties                   = useCallback(
              <K extends Item | Reference>( originalObject : K, changedObject : K, fullData : boolean = true ) : K extends Item ? ItemAttributesSettable : ReferenceAttributesSettable => {
                  const originalEnumProperties = originalObject.enumProperties[ Symbol.iterator ](),
                        originalProperties     = originalObject.properties[ Symbol.iterator ](),
                        deletedStatusId        = metaModel[ schema ].getStatus( 'deleted' )!.id;

                  let retVal : ItemAttributesSettable | ReferenceAttributesSettable;

                  if ( changedObject instanceof Item ) {
                      retVal = ( fullData ? changedObject.toUpdateJSON( 'all' ) : changedObject.toUpdateJSON( 'modified' ) ) as ItemAttributesSettable;
                  }
                  else {
                      retVal = ( fullData ? changedObject.toUpdateJSON( 'all' ) : changedObject.toUpdateJSON( 'modified' ) ) as ReferenceAttributesSettable;
                  }

                  // handle deleted new enumProperty properties
                  for ( const [ propertyTypeId, enumProperty ] of originalEnumProperties ) {
                      const originalEnumPropertyProperties = enumProperty.properties[ Symbol.iterator ](),
                            changedEnumProperty            = changedObject.enumProperties.get( propertyTypeId );

                      for ( const [ key, property ] of originalEnumPropertyProperties ) {
                          if ( property.isNew() && !changedEnumProperty?.properties.has( key ) ) {
                              if ( !retVal.enumProperties ) {
                                  retVal.enumProperties = {};
                              }
                              if ( !retVal.enumProperties[ propertyTypeId ] ) {
                                  retVal.enumProperties[ propertyTypeId ] = {
                                      properties: {}
                                  };
                              }
                              retVal.enumProperties[ propertyTypeId ].properties![ key ] = {
                                  statusId: deletedStatusId
                              };
                          }
                      }
                  }

                  // handle deleted new properties
                  for ( const [ propertyTypeId, property ] of originalProperties ) {
                      if ( property.isNew() && !changedObject.properties.has( propertyTypeId ) ) {
                          if ( !retVal.properties ) {
                              retVal.properties = {};
                          }
                          retVal.properties[ propertyTypeId ] = {
                              statusId: deletedStatusId
                          };
                      }
                  }

                  return retVal;
              },
              [
                  'onlyOnce'
              ]
          ),
          processBackgroundEvents                                 = useCallback(
              () => {
                  const calendarWrapperElement = document.getElementById( `${uid}_wrapper` ),
                        processCell            = ( cell : HTMLElement, cellBackgroundColor? : string, processEvents : boolean = false, moveEventsToHeader : boolean = false ) => {
                            if ( cellBackgroundColor ) {
                                cell.style.backgroundImage = `linear-gradient(to right, ${cellBackgroundColor}, ${cellBackgroundColor})`;
                                cell.classList.add( 'yn-no-highlight' );
                            }
                            else {
                                cell.style.backgroundImage = 'none';
                                cell.classList.remove( 'yn-no-highlight' );
                            }

                            // for background events
                            // - make the top-most element of
                            if ( processEvents ) {
                                const eventElements = cell.querySelectorAll( '.ec-events .ec-event' ) as NodeListOf<HTMLElement>;

                                for ( const eventElement of eventElements ) {
                                    const eventTitleElement = eventElement.querySelector( '.ec-event-body .ec-event-title' ),
                                          backgroundEvent   = eventTitleElement?.innerHTML
                                              ? Object.values( backgroundEvents )
                                                  .find( eA => eA.find( e => e.title === eventTitleElement.innerHTML ) )
                                                  ?.find( e => e.title === eventTitleElement.innerHTML )
                                              : undefined;

                                    if ( backgroundEvent ) {
                                        if ( !eventElement.classList.contains( 'yn-background-event' ) ) {
                                            let parent = eventElement.parentElement!;

                                            if ( moveEventsToHeader ) {
                                                parent = parent.parentElement!.querySelector( '.ec-day-head' ) as HTMLElement;
                                            }

                                            eventElement.classList.add( 'yn-background-event' );
                                            eventElement.remove();

                                            parent.prepend( eventElement );
                                        }
                                    }
                                }
                            }
                        };

                  if ( calendarWrapperElement ) {
                      const backgroundEventDates = Object.keys( backgroundEvents );

                      let headerCells : NodeListOf<HTMLElement> | undefined,
                          allDayCells : NodeListOf<HTMLElement> | undefined,
                          bodyCells : NodeListOf<HTMLElement> | undefined;

                      if ( selectedView === 'dayGridMonth' ) {
                          headerCells = calendarWrapperElement.querySelectorAll( '.ec-body .ec-content .ec-days .ec-day time:not(.ec-event-time)' ) as NodeListOf<HTMLElement>;
                          bodyCells = calendarWrapperElement.querySelectorAll( '.ec-body .ec-content .ec-days .ec-day' ) as NodeListOf<HTMLElement>;
                      }
                      else {
                          headerCells = calendarWrapperElement.querySelectorAll( '.ec-header .ec-days .ec-day time:not(.ec-event-time)' ) as NodeListOf<HTMLElement>;
                          allDayCells = calendarWrapperElement.querySelectorAll( '.ec-all-day .ec-content .ec-days .ec-day' ) as NodeListOf<HTMLElement>;
                          bodyCells = calendarWrapperElement.querySelectorAll( '.ec-body .ec-content .ec-days .ec-day' ) as NodeListOf<HTMLElement>;
                      }

                      for ( let headerCellIndex = 0; headerCellIndex < headerCells.length; headerCellIndex++ ) {
                          const headerCellDate = headerCells[ headerCellIndex ].getAttribute( 'datetime' );

                          let backgroundColor = '';

                          if ( headerCellDate && backgroundEventDates.includes( headerCellDate ) ) {
                              for ( const event of backgroundEvents[ headerCellDate ] ) {
                                  if ( event?.backgroundColor ) {
                                      backgroundColor = event.backgroundColor;
                                      break;
                                  }
                              }
                          }

                          if ( selectedView !== 'dayGridMonth' && headerCells[ headerCellIndex ].parentElement ) {
                              processCell( headerCells[ headerCellIndex ].parentElement as HTMLElement, backgroundColor );
                          }

                          if ( allDayCells?.[ headerCellIndex ] ) {
                              processCell( allDayCells[ headerCellIndex ], backgroundColor, true );
                          }

                          if ( bodyCells?.[ headerCellIndex ] ) {
                              processCell( bodyCells[ headerCellIndex ], backgroundColor, true, selectedView === 'dayGridMonth' && !!backgroundEvents[ headerCellDate ?? '' ]?.find( e => e.allDay ) );
                          }
                      }
                  }
              },
              [
                  backgroundEvents,
                  selectedView
              ]
          ),
          hideEditItemDialog                                      = useCallback(
              () => {
                  if ( editItem.current ) {
                      editItem.current = null;
                      editItemDataContext.current = dataContext;
                  }
                  if ( editFormDataContext !== dataContext ) {
                      delete global.data[ editFormDataContext ];
                  }

                  setShowEditItemDialog( false );

                  setTimeout(
                      () => {
                          dispatch( clearPanelProps( editFormUid ) );
                          dispatch( clearElusiveData( editFormUid ) );
                          dispatch( clearElusiveDataForSubpanels( editFormUid ) );
                      },
                      1
                  );
              },
              [
                  dataContext,
                  editFormDataContext,
                  editFormUid
              ]
          ),
          onEditItemDialogHide                                    = useCallback(
              () => {
                  hideEditItemDialog();
              },
              [
                  'onlyOnce'
              ]
          ),
          onEditItemDialogOkButtonClick                           = useCallback(
              () => {
                  const startTime = debug ? performance.now() : 0;

                  if ( debug ) {
                      console.groupCollapsed( `%c${`%cCalendar - %conEditItemDialogOkButtonClick%c`.asLogLine( 97 )}`, `font-weight: normal;`, `font-weight: bold;`, `font-weight: bold;`, 'color: unset; font-weight: normal;' );
                  }

                  setShowEditItemDialog( false );

                  if ( editItem.current ) {
                      const data : ItemsAndReferencesCollection | undefined = {
                                items     : Object.values( global.data[ editFormDataContext ].items ?? {} ),
                                references: Object.values( global.data[ editFormDataContext ].references ?? {} )
                            },
                            itemIdMapping : { [ id : number ] : number }    = {};

                      for ( const item of data.items ) {
                          // update existing/new item
                          if ( global.data[ editItemDataContext.current ].items[ item.id ] ) {
                              const itemToUpdate = global.data[ editItemDataContext.current ].items[ item.id ];

                              if ( item.isDeleted() ) {
                                  if ( item.statusId !== itemToUpdate.statusId ) {
                                      if ( debug ) {
                                          console.log( '    item' );
                                          console.log( '        itemId', item.id );
                                          console.log( '        -> delete existing' );
                                      }

                                      dispatch( deleteItem(
                                          editItemDataContext.current,
                                          item.id,
                                          true,
                                          additionalElusiveDataContexts
                                      ) );
                                  }
                              }
                              else {
                                  if ( JSON.stringify( item.toUpdateJSON( 'modified' ) ) !== JSON.stringify( itemToUpdate.toUpdateJSON( 'modified' ) ) ) {
                                      if ( debug ) {
                                          console.log( '    item' );
                                          console.log( '        itemId', item.id );
                                          console.log( '        -> update' );
                                      }

                                      const itemData = getUpdateDataWithRemovedNewProperties( itemToUpdate, item, false );

                                      if ( debug ) {
                                          console.log( '                data', itemData );
                                      }

                                      dispatch( updateItem(
                                          editItemDataContext.current,
                                          item.id,
                                          itemData,
                                          additionalElusiveDataContexts
                                      ) );
                                  }
                              }
                          }
                          // add existing item
                          else if ( item.id > 0 ) {
                              if ( debug ) {
                                  console.log( '    item' );
                                  console.log( '        itemId', item.id );
                                  console.log( '        -> add' );
                              }

                              let itemData : ItemAttributesSettable | undefined;

                              if ( item.isModified() ) {
                                  itemData = item.toUpdateJSON( 'modified' );

                                  item.reset();
                              }

                              if ( debug ) {
                                  console.log( '                data', itemData );
                              }

                              dispatch( addItem(
                                  editItemDataContext.current,
                                  { ...item.toJSON( 'all' ) } as ItemAttributesForCreation,
                                  schema,
                                  additionalElusiveDataContexts
                              ) );

                              if ( itemData ) {
                                  dispatch( updateItem(
                                      editItemDataContext.current,
                                      item.id,
                                      itemData,
                                      additionalElusiveDataContexts
                                  ) );

                                  item.update( itemData );
                              }
                          }
                          // add new item
                          else {
                              if ( debug ) {
                                  console.log( '    item' );
                                  console.log( '        itemId', item.id );
                                  console.log( '        -> create' );
                              }

                              const newItem  = dispatch( addItem(
                                        editItemDataContext.current,
                                        { itemTypeId: item.itemTypeId } as ItemAttributesForCreation,
                                        schema,
                                        additionalElusiveDataContexts
                                    ) ),
                                    itemData = item.toUpdateJSON( 'all' );

                              if ( debug ) {
                                  console.log( '            data', itemData );
                              }

                              dispatch( updateItem(
                                  editItemDataContext.current,
                                  newItem.id,
                                  itemData,
                                  additionalElusiveDataContexts
                              ) );

                              // add id mapping
                              itemIdMapping[ item.id ] = newItem.id;
                          }
                      }

                      // handle deleted new references
                      for ( const reference of Object.values( global.data[ editItemDataContext.current ].references ) ) {
                          if ( reference.isNew() && !data.references.find( r => r.id === reference.id ) ) {
                              if ( debug ) {
                                  console.log( '    reference' );
                                  console.log( '        referenceId', reference.id );
                                  console.log( '        referenceSetId', reference.referenceSetId );
                                  console.log( '        -> delete new' );
                              }

                              dispatch( deleteReference(
                                  editItemDataContext.current,
                                  reference.id,
                                  true,
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      for ( const reference of data.references ) {
                          // update existing/new reference
                          if ( global.data[ editItemDataContext.current ].references[ reference.id ] ) {
                              const referenceToUpdate = global.data[ editItemDataContext.current ].references[ reference.id ];

                              if ( reference.isDeleted() ) {
                                  if ( reference.statusId !== referenceToUpdate.statusId ) {
                                      if ( debug ) {
                                          console.log( '    reference' );
                                          console.log( '        referenceId', reference.id );
                                          console.log( '        referenceSetId', reference.referenceSetId );
                                          console.log( '        -> delete existing' );
                                      }

                                      dispatch( deleteReference(
                                          editItemDataContext.current,
                                          reference.id,
                                          true,
                                          additionalElusiveDataContexts
                                      ) );
                                  }
                              }
                              else {
                                  if ( JSON.stringify( reference.toUpdateJSON( 'modified' ) ) !== JSON.stringify( referenceToUpdate.toUpdateJSON( 'modified' ) ) ) {
                                      if ( debug ) {
                                          console.log( '    reference' );
                                          console.log( '        referenceId', reference.id );
                                          console.log( '        referenceSetId', reference.referenceSetId );
                                          console.log( '        -> update' );
                                      }

                                      const referenceData = getUpdateDataWithRemovedNewProperties( referenceToUpdate, reference, false );

                                      if ( debug ) {
                                          console.log( '                data', referenceData );
                                      }

                                      dispatch( updateReference(
                                          editItemDataContext.current,
                                          reference.id,
                                          referenceData,
                                          additionalElusiveDataContexts
                                      ) );
                                  }
                              }
                          }
                          // add existing reference
                          else if ( reference.id > 0 ) {
                              if ( debug ) {
                                  console.log( '    reference' );
                                  console.log( '        referenceId', reference.id );
                                  console.log( '        referenceSetId', reference.referenceSetId );
                                  console.log( '        -> add' );
                              }

                              let referenceData : ReferenceAttributesSettable | undefined;

                              if ( reference.isModified() ) {
                                  referenceData = reference.toUpdateJSON( 'modified' );

                                  reference.reset();
                              }

                              if ( debug ) {
                                  console.log( '                data', referenceData );
                              }

                              dispatch( addReference(
                                  editItemDataContext.current,
                                  { ...reference.toJSON( 'all' ) } as ReferenceAttributesForCreation,
                                  schema,
                                  additionalElusiveDataContexts
                              ) );

                              if ( referenceData ) {
                                  dispatch( updateReference(
                                      editItemDataContext.current,
                                      reference.id,
                                      referenceData,
                                      additionalElusiveDataContexts
                                  ) );

                                  reference.update( referenceData );
                              }
                          }
                          // add new reference
                          else {
                              if ( debug ) {
                                  console.log( '    reference' );
                                  console.log( '        referenceId', reference.id );
                                  console.log( '        referenceSetId', reference.referenceSetId );
                                  console.log( '        -> create' );
                              }

                              const newReference  = dispatch( addReference(
                                        editItemDataContext.current,
                                        {
                                            referenceSetId: reference.referenceSetId,
                                            srcItemId     : itemIdMapping[ reference.srcItemId ] ?? reference.srcItemId,
                                            destItemId    : itemIdMapping[ reference.destItemId ] ?? reference.destItemId
                                        } as ReferenceAttributesForCreation,
                                        schema,
                                        additionalElusiveDataContexts
                                    ) ),
                                    referenceData = reference.toUpdateJSON( 'all' );

                              if ( debug ) {
                                  console.log( '            data', referenceData );
                              }

                              dispatch( updateReference(
                                  editItemDataContext.current,
                                  newReference.id,
                                  referenceData,
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }
                  }

                  hideEditItemDialog();

                  if ( debug ) {
                      console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 96 ), `font-weight: bold;`, 'color: unset; font-weight: unset;' );
                      console.groupEnd();
                  }
              },
              [
                  additionalElusiveDataContexts,
                  getUpdateDataWithRemovedNewProperties,
                  schema
              ]
          ),
          onEditItemDialogCancelButtonClick                       = useCallback(
              () => {
                  hideEditItemDialog();
              },
              [
                  'onlyOnce'
              ]
          ),
          hideNewItemSelectDialog                                 = useCallback(
              () => {
                  setShowNewItemSelectDialog( false );
                  setNewItemEventProps( null );
                  setSelectedCalendarEvent( undefined );
              },
              [
                  'onlyOnce'
              ]
          ),
          onNewItemSelectDialogHide                               = useCallback(
              () => {
                  hideNewItemSelectDialog();
              },
              [
                  'onlyOnce'
              ]
          ),
          onNewItemSelectDialogOkButtonClick                      = useCallback(
              () => {
                  setShowNewItemSelectDialog( false );

                  if ( selectedCalendarEvent !== undefined && editableCalendarEvents[ selectedCalendarEvent ] && newItemEventProps ) {
                      createNewEvent( editableCalendarEvents[ selectedCalendarEvent ].calendarEvent, editableCalendarEvents[ selectedCalendarEvent ].dataStructure, editableCalendarEvents[ selectedCalendarEvent ].dataContext, newItemEventProps.allDay, newItemEventProps.start, newItemEventProps.end );
                  }

                  hideNewItemSelectDialog();
              },
              [
                  editableCalendarEvents,
                  newItemEventProps,
                  selectedCalendarEvent
              ]
          ),
          onNewItemSelectDialogCancelButtonClick                  = useCallback(
              () => {
                  hideNewItemSelectDialog();
              },
              [
                  'onlyOnce'
              ]
          ),
          onEventClick                                            = useCallback(
              ( info : EventCalendar.EventClickInfo ) => {
                  const extendedProps = info.event.extendedProps as ExtendedProps;

                  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

                  // open edit popup if editable
                  if ( info.event.editable && extendedProps?.rootItemId ) {
                      hideEditItemDialog();

                      // clone dataContext
                      for ( const item of Object.values( global.data[ extendedProps.dataContext ].items ) ) {
                          item.clone( undefined, undefined, editFormDataContext );
                      }
                      for ( const reference of Object.values( global.data[ extendedProps.dataContext ].references ) ) {
                          reference.clone( undefined, undefined, editFormDataContext );
                      }

                      editItem.current = global.data[ editFormDataContext ].items[ extendedProps.rootItemId ];
                      editItemDataContext.current = extendedProps.dataContext;

                      setShowEditItemDialog( true );

                      setEditFormVariables( undefined );
                      setEditFormKey( extendedProps.editFormKey );
                      setEditFormTitle( extendedProps.editFormTitle ?? __( 'YN.base.form.referenceField.item.edit', { itemTypeName: metaModel[ schema ].getItemType( editItem.current.itemType.id ?? 0 )?.label ?? '' } ) );
                  }

                  // open panel
                  else if (
                      !extendedProps?.background
                      && extendedProps?.rootItemSchema
                      && extendedProps.rootItemTypeName
                      && (
                          extendedProps.rootItemId
                          || extendedProps.rootItemKey
                      )
                  ) {
                      dispatch( openPanelForItemType(
                          extendedProps.rootItemSchema,
                          extendedProps.rootItemTypeName,
                          {
                              schema: extendedProps.rootItemSchema,
                              //
                              rootItemId: extendedProps.rootItemId,
                              //
                              rootItemKey : !extendedProps.rootItemId ? extendedProps.rootItemKey : undefined,
                              rootItemType: !extendedProps.rootItemId ? extendedProps.rootItemTypeName : undefined
                          },
                          undefined,
                          info.jsEvent as unknown as MouseEvent<HTMLElement>
                      ) );
                  }

                  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
              },
              [
                  isEditable,
                  queryType,
                  schema
              ]
          ),
          onEventDidMount                                         = useCallback(
              ( info : EventCalendar.EventDidMountInfo ) => {
                  if ( info.event.extendedProps?.isDirty ) {
                      info.el.classList.add( 'yn-dirty-border' );
                  }
                  else {
                      info.el.classList.remove( 'yn-dirty-border' );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onEventMouseEnter                                       = useCallback(
              ( info : EventCalendar.MouseEnterInfo ) => {
                  let timeText = '';

                  if ( info.event.allDay ) {
                      const start = new Intl.DateTimeFormat( undefined, { day: '2-digit', month: '2-digit', year: 'numeric' } ).format( info.event.start ),
                            end   = new Intl.DateTimeFormat( undefined, { day: '2-digit', month: '2-digit', year: 'numeric' } ).format( moment( info.event.end ).subtract( 1, 'second' ).toDate() );

                      timeText = start;

                      if ( start !== end ) {
                          timeText += ' - ' + end;
                      }
                  }
                  else if ( eventCalendar.current ) {
                      timeText = new Intl.DateTimeFormat( undefined, eventCalendar.current.getOption( 'eventTimeFormat' ) as Intl.DateTimeFormatOptions ).formatRange( info.event.start, info.event.end );
                  }

                  setTooltip( { element: info.el, content: <>{timeText}<br/>{info.event.title}</> } );
              },
              [
                  'onlyOnce'
              ]
          ),
          onEventMouseLeave                                       = useCallback(
              () => {
                  setTooltip( undefined );
              },
              [
                  'onlyOnce'
              ]
          ),
          onNewEventClick                                         = useCallback(
              ( info : EventCalendar.DateClickInfo ) => {
                  if ( editableCalendarEvents?.length ) {
                      const start = info.date,
                            end   = info.date;

                      if ( selectedView === 'dayGridMonth' ) {
                          start.setHours( ...( slotMinTime.split( ':' ).map( d => Number( d ) ) as [ number, number, number ] ) );
                          end.setHours( ...( slotMaxTime.split( ':' ).map( d => Number( d ) ) as [ number, number, number ] ) );
                      }
                      else {
                          end.setHours( start.getHours() + 1 );
                      }

                      if ( editableCalendarEvents.length > 1 ) {
                          setShowNewItemSelectDialog( true );
                          setNewItemEventProps( {
                              allDay: info.allDay,
                              start,
                              end
                          } );
                          setSelectedCalendarEvent( 0 );
                      }
                      else {
                          createNewEvent( editableCalendarEvents[ 0 ].calendarEvent, editableCalendarEvents[ 0 ].dataStructure, editableCalendarEvents[ 0 ].dataContext, info.allDay, start, end );
                      }
                  }
              },
              [
                  editableCalendarEvents
              ]
          ),
          onNewEventSelect                                        = useCallback(
              ( info : EventCalendar.SelectInfo ) => {
                  if ( editableCalendarEvents?.length ) {
                      const start = info.start,
                            end   = info.end;

                      if ( selectedView === 'dayGridMonth' ) {
                          start.setHours( ...( slotMinTime.split( ':' ).map( d => Number( d ) ) as [ number, number, number ] ) );
                          end.setHours( ...( slotMaxTime.split( ':' ).map( d => Number( d ) ) as [ number, number, number ] ) );
                      }

                      if ( editableCalendarEvents.length > 1 ) {
                          setShowNewItemSelectDialog( true );
                          setNewItemEventProps( {
                              allDay: info.allDay,
                              start,
                              end
                          } );
                          setSelectedCalendarEvent( 0 );
                      }
                      else {
                          createNewEvent( editableCalendarEvents[ 0 ].calendarEvent, editableCalendarEvents[ 0 ].dataStructure, editableCalendarEvents[ 0 ].dataContext, info.allDay, start, end );
                      }
                  }
              },
              [
                  editableCalendarEvents
              ]
          ),
          onEventStartChange                                      = useCallback(
              ( info : EventCalendar.EventDropInfo ) => {
                  const startTime     = debug ? performance.now() : 0,
                        extendedProps = info.event.extendedProps as ExtendedProps;

                  if ( debug ) {
                      console.groupCollapsed( `%c${`%cCalendar - %conEventStartChange%c`.asLogLine( 97 )}`, `font-weight: normal;`, `font-weight: bold;`, `font-weight: bold;`, 'color: unset; font-weight: normal;' );
                  }

                  if (
                      queryType === 'DataStructure'
                      && extendedProps?.rootItemId
                      && extendedProps?.calendarEvent.rootDataDescriptor
                  ) {
                      if ( extendedProps?.calendarEvent.fromDataDescriptor ) {
                          if ( debug ) {
                              console.log( '    from' );
                              console.log( '        value', info.event.start );
                          }

                          updateDataDescriptorData( extendedProps.calendarEvent.rootDataDescriptor.dataObject.id, extendedProps.rootItemId, extendedProps.calendarEvent.fromDataDescriptor, extendedProps.dataContext, extendedProps.dataStructure, info.event.start );
                      }

                      if ( extendedProps?.calendarEvent.toDataDescriptor ) {
                          if ( debug ) {
                              console.log( '    to' );
                              console.log( '        value', info.event.end );
                          }
                          updateDataDescriptorData( extendedProps.calendarEvent.rootDataDescriptor.dataObject.id, extendedProps.rootItemId, extendedProps.calendarEvent.toDataDescriptor, extendedProps.dataContext, extendedProps.dataStructure, info.event.end )
                      }
                  }

                  if ( debug ) {
                      console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 96 ), `font-weight: bold;`, 'color: unset; font-weight: unset;' );
                      console.groupEnd();
                  }
              },
              [
                  queryType,
                  updateDataDescriptorData
              ]
          ),
          onEventEndChange                                        = useCallback(
              ( info : EventCalendar.EventResizeInfo ) => {
                  const extendedProps = info.event.extendedProps as ExtendedProps;

                  if (
                      queryType === 'DataStructure'
                      && extendedProps?.rootItemId
                      && extendedProps?.calendarEvent.rootDataDescriptor
                  ) {
                      if ( extendedProps?.calendarEvent.fromDataDescriptor ) {
                          updateDataDescriptorData( extendedProps.calendarEvent.rootDataDescriptor.dataObject.id, extendedProps.rootItemId, extendedProps.calendarEvent.fromDataDescriptor, extendedProps.dataContext, extendedProps.dataStructure, info.event.start );
                      }

                      if ( extendedProps?.calendarEvent.toDataDescriptor ) {
                          updateDataDescriptorData( extendedProps.calendarEvent.rootDataDescriptor.dataObject.id, extendedProps.rootItemId, extendedProps.calendarEvent.toDataDescriptor, extendedProps.dataContext, extendedProps.dataStructure, info.event.end )
                      }
                  }
              },
              [
                  queryType,
                  updateDataDescriptorData
              ]
          ),
          onViewDidMount                                          = useCallback(
              ( info : { view : EventCalendar.View; } ) => {
                  const view = info as unknown as EventCalendar.View;

                  if ( setFormPersistentSubPanelProps ) {
                      setFormPersistentSubPanelProps( 'selectedView', view.type as SelectedViews );
                  }

                  dispatch( setParam( uid, 'selectedView', view.type as SelectedViews ) );
              },
              [
                  formPersistentSubPanelProps,
                  uid
              ]
          ),
          onDatesSet                                              = useCallback(
              ( info : EventCalendar.DatesSetInfo ) => {
                  const newStartDate = moment( info.start ).tz( 'utc', true ).format( 'YYYY-MM-DD' ),
                        newEndDate   = moment( info.end ).tz( 'utc', true ).format( 'YYYY-MM-DD' );

                  console.log( 'onDatesSet'.asLogLine( 100 ) );
                  console.log( 'view', info.view.type );

                  let start = info.start,
                      end   = info.end;

                  // set to first of month
                  // since start of displayed month view could be in the month before
                  // so add one week to be sure to be within the right month
                  if ( info.view.type === 'dayGridMonth' ) {
                      // add one week to be sure to be in the right month
                      const rightMonth = new Date( info.start.getTime() + 1000 * 60 * 60 * 24 * 7 );

                      // first of month
                      start = new Date( Date.UTC( rightMonth.getFullYear(), rightMonth.getMonth(), 1, rightMonth.getHours(), rightMonth.getMinutes() ) );
                      // last of month
                      end = new Date( Date.UTC( rightMonth.getFullYear(), rightMonth.getMonth() + 1, 0, rightMonth.getHours(), rightMonth.getMinutes() ) );
                  }

                  if ( setFormPersistentSubPanelProps ) {
                      setFormPersistentSubPanelProps( 'selectedView', info.view.type as SelectedViews );
                      setFormPersistentSubPanelProps( 'startDate', moment( start ).tz( 'utc', true ).format( 'YYYY-MM-DD' ) );
                      setFormPersistentSubPanelProps( 'viewStartDate', newStartDate );
                      setFormPersistentSubPanelProps( 'viewEndDate', newEndDate );
                  }

                  dispatch( setParams( uid, {
                      selectedView : info.view.type as SelectedViews,
                      startDate    : moment( start ).tz( 'utc', true ).format( 'YYYY-MM-DD' ),
                      viewStartDate: newStartDate,
                      viewEndDate  : newEndDate
                  } ) );

                  dispatch( setElusiveData( uid, { dataLoaded: false } ) );

                  onRangeChange?.(
                      start,
                      end
                  );
              },
              [
                  uid,
                  onRangeChange,
                  setFormPersistentSubPanelProps
              ]
          ),
          setCalenderLanguageOptions                              = useCallback(
              () => {
                  if ( eventCalendar.current ) {
                      const locale = __( 'CP.Base.SupportedLanguages.locale' );

                      eventCalendar.current.setOption( 'locale', locale !== 'CP.Base.SupportedLanguages.locale' ? locale : 'de-DE' );
                      eventCalendar.current.setOption( 'firstDay', language === 'EN' ? 0 : 1 );
                      eventCalendar.current.setOption( 'allDayContent', __( 'YN.base.common.allDay' ) );
                      eventCalendar.current.setOption( 'buttonText', {
                          close               : __( 'YN.base.common.close' ),
                          dayGridMonth        : __( 'YN.base.common.month' ),
                          listDay             : __( 'YN.base.common.list' ),
                          listMonth           : __( 'YN.base.common.list' ),
                          listWeek            : __( 'YN.base.common.list' ),
                          listYear            : __( 'YN.base.common.list' ),
                          resourceTimeGridDay : __( 'YN.base.common.day' ),
                          resourceTimeGridWeek: __( 'YN.base.common.week' ),
                          timeGridDay         : __( 'YN.base.common.day' ),
                          timeGridWeek        : __( 'YN.base.common.week' ),
                          today               : __( 'YN.base.common.today' )
                      } );
                  }
              },
              [
                  language,
                  messageCatalogTimestamp
              ]
          ),
          setCalenderViewOptions                                  = useCallback(
              () => {
                  if ( eventCalendar.current ) {
                      eventCalendar.current.setOption( 'date', startDate ?? moment().tz( 'utc', true ).format( 'YYYY-MM-DD' ) );
                      eventCalendar.current.setOption( 'firstDay', firstDayOfWeek );
                      eventCalendar.current.setOption( 'flexibleSlotTimeLimits', flexibleSlotTimeLimits );
                      eventCalendar.current.setOption( 'slotMaxTime', slotMaxTime );
                      eventCalendar.current.setOption( 'slotMinTime', slotMinTime );
                      eventCalendar.current.setOption( 'view', selectedView ?? 'dayGridMonth' );
                  }
              },
              [
                  firstDayOfWeek,
                  flexibleSlotTimeLimits,
                  selectedView,
                  slotMaxTime,
                  slotMinTime,
                  startDate
              ]
          ),
          setCalenderEditableOptions                              = useCallback(
              () => {
                  if ( eventCalendar.current ) {
                      eventCalendar.current.setOption( 'editable', isEditable );
                      eventCalendar.current.setOption( 'eventStartEditable', isEditable );
                      eventCalendar.current.setOption( 'eventDurationEditable', isEditable );
                      eventCalendar.current.setOption( 'selectable', isEditable );

                      eventCalendar.current.setOption( 'viewDidMount', onViewDidMount );
                      eventCalendar.current.setOption( 'datesSet', onDatesSet );
                      eventCalendar.current.setOption( 'eventClick', onEventClick );
                      eventCalendar.current.setOption( 'eventMouseEnter', onEventMouseEnter );
                      eventCalendar.current.setOption( 'eventMouseLeave', onEventMouseLeave );
                      eventCalendar.current.setOption( 'dateClick', isEditable ? onNewEventClick : undefined );
                      eventCalendar.current.setOption( 'select', isEditable ? onNewEventSelect : undefined );
                      eventCalendar.current.setOption( 'eventDrop', isEditable ? onEventStartChange : undefined );
                      eventCalendar.current.setOption( 'eventResize', isEditable ? onEventEndChange : undefined );
                      eventCalendar.current.setOption( 'eventDidMount', isEditable ? onEventDidMount : undefined );
                  }
              },
              [
                  isEditable,
                  onDatesSet,
                  onEventClick,
                  onEventDidMount,
                  onEventEndChange,
                  onEventMouseEnter,
                  onEventMouseLeave,
                  onEventStartChange,
                  onNewEventClick,
                  onNewEventSelect,
                  onViewDidMount
              ]
          ),
          newItemSelectDialog                                     = useMemo(
              () => {
                  let retVal : React.ReactNode | null = null;

                  if ( showNewItemSelectDialog && selectedCalendarEvent !== undefined && editableCalendarEvents?.length > 1 ) {
                      const data : { id : number; calendarEvent : EditableCalendarEvent; label : string; }[] = [];

                      for ( let index = 0; index < editableCalendarEvents.length; index++ ) {
                          const editableCalendarEvent = editableCalendarEvents[ index ];

                          if ( editableCalendarEvent.calendarEvent.rootDataDescriptor ) {
                              const newItemSchema = ( editableCalendarEvent.calendarEvent.rootDataDescriptor.dataObject.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema,
                                    newItemType   = metaModel[ newItemSchema ].getItemType( editableCalendarEvent.calendarEvent.rootDataDescriptor.dataObject.item.objectType ?? '' );

                              if ( newItemType ) {
                                  data.push( {
                                      id           : index,
                                      calendarEvent: editableCalendarEvent,
                                      label        : newItemType.label
                                  } );
                              }
                          }
                      }

                      retVal = <Dialog
                          key={`${uid}_newItemSelectDialog`}
                          id={`${uid}_newItemSelectDialog`}

                          visible={true}
                          onHide={onNewItemSelectDialogHide}
                          focusOnShow={false}
                          closeOnEscape={false}
                          className="yn-form-dialog"
                          style={{
                              width            : '33rem',
                              minWidth         : '33rem',
                              [ '--min-width' ]: '33rem',
                              minHeight        : '20vh'
                          }}
                          header={__( 'YN.base.frontend.calendar.newItem.select.title' )}
                          footer={<div>
                              <Button
                                  id={`${editFormUid}_okButton`}
                                  key="okButton"
                                  label={__( 'YN.base.common.ok' )}
                                  className="p-button-outlined mr-3"
                                  onClick={onNewItemSelectDialogOkButtonClick}
                              />
                              <Button
                                  id={`${editFormUid}_cancelButton`}
                                  key="cancelButton"
                                  label={__( 'YN.base.common.cancel' )}
                                  className="p-button-secondary p-button-outlined"
                                  onClick={onNewItemSelectDialogCancelButtonClick}
                              />
                          </div>}
                      >
                          <Dropdown
                              key={`${uid}_newItemSelect`}
                              id={`${uid}_newItemSelect`}

                              value={data[ selectedCalendarEvent ]}
                              options={data}
                              dataKey="id"
                              optionLabel="label"
                              onChange={( event ) => {
                                  setSelectedCalendarEvent( event.value.id );
                              }}
                          />
                      </Dialog>;
                  }

                  return retVal;
              },
              [
                  editableCalendarEvents,
                  onNewItemSelectDialogHide,
                  onNewItemSelectDialogOkButtonClick,
                  selectedCalendarEvent,
                  showNewItemSelectDialog,
                  uid
              ]
          ),
          editForm                                                = useMemo(
              () => {
                  let retVal : React.ReactNode | null = null;

                  if ( editItem.current && showEditItemDialog ) {
                      retVal = <Dialog
                          key={`${uid}_editItemDialog`}
                          id={`${uid}_editItemDialog`}

                          visible={true}
                          onHide={onEditItemDialogHide}
                          focusOnShow={false}
                          closeOnEscape={false}
                          className="yn-form-dialog"
                          header={editFormTitle ?? __( 'YN.base.form.referenceField.item.create', { itemTypeName: metaModel[ schema ].getItemType( editItem.current.itemType.id ?? 0 )?.label ?? '' } )}
                          footer={<div>
                              <Button
                                  id={`${editFormUid}_okButton`}
                                  key="okButton"
                                  label={__( 'YN.base.common.ok' )}
                                  className="p-button-outlined mr-3"
                                  disabled={Boolean( editFormElusiveData?.errorData?.count ?? 1 )}
                                  onClick={onEditItemDialogOkButtonClick}
                              />
                              <Button
                                  id={`${editFormUid}_cancelButton`}
                                  key="cancelButton"
                                  label={__( 'YN.base.common.cancel' )}
                                  className="p-button-secondary p-button-outlined"
                                  onClick={onEditItemDialogCancelButtonClick}
                              />
                          </div>}
                      >
                          <LoadingMaskPanel key={`${editFormUid}_loadingMask`} forComponent={editFormUid} className="yn-flex-layout-parent">
                              {editFormKey &&
                                  <Form
                                      {...additionalAddItemFormProps}

                                      key={editFormUid}
                                      uid={editFormUid}
                                      dataContext={editFormDataContext}
                                      additionalCheckDataContexts={[ dataContext ]}

                                      schema={schema}
                                      viewKey={editFormKey}
                                      rootItemId={editItem.current.id}
                                      mode="popup"

                                      tabIndexOffset={tabIndexOffset}

                                      onDataLoad={() => {
                                          setForceRender( forceRender + 1 );
                                      }}

                                      treeVariables={editFormVariables}

                                      {...editFormProps}
                                  />
                              }
                              {!editFormKey &&
                                  <GenericForm
                                      key={editFormUid}
                                      uid={editFormUid}
                                      dataContext={editFormDataContext}
                                      additionalCheckDataContexts={[ dataContext ]}

                                      schema={schema}
                                      item={editItem.current}
                                      editable

                                      onErrorDataChanged={() => {
                                          setForceRender( forceRender + 1 );
                                      }}

                                      {...editFormProps}
                                  />
                              }
                          </LoadingMaskPanel>
                      </Dialog>;
                  }

                  return retVal;
              },
              [
                  additionalAddItemFormProps,
                  dataContext,
                  editFormDataContext,
                  editFormElusiveData,
                  editFormKey,
                  editFormProps,
                  editFormTitle,
                  editFormUid,
                  editFormVariables,
                  editItem.current,
                  forceRender,
                  onEditItemDialogCancelButtonClick,
                  onEditItemDialogHide,
                  onEditItemDialogOkButtonClick,
                  schema,
                  showEditItemDialog,
                  tabIndexOffset,
                  uid
              ]
          ),
          content                                                 = useMemo(
              () => {
                  return <>
                      <div
                          key={`${uid}_wrapper`}
                          id={`${uid}_wrapper`}
                      ></div>
                      {tooltip &&
                          <Tooltip
                              target={tooltip.element}
                              position="mouse"
                              mouseTrack={true}
                              className="yn-tooltip"
                              showEvent="mouseover"
                          >
                              {tooltip.content}
                          </Tooltip>
                      }
                      {newItemSelectDialog}
                      {editForm}
                  </>;
              },
              [
                  editForm,
                  newItemSelectDialog,
                  tooltip,
                  uid
              ]
          );


    dirtyDataContexts.current = useMemo(
        () => {
            const retVal : string[] = [];

            if ( queryType === 'DataStructure' ) {

                // 1. check (main) calendar
                if ( dataStructure && calendarEvents && checkIsDirty( dataContext, dataStructure, calendarEvents ) ) {
                    retVal.push( dataContext );
                }

                //  2. check calendars
                for ( const calendar of calendars ?? [] ) {
                    if ( checkIsDirty( `${dataContext}-${calendar.key}`, calendar.dataStructure, calendar.calendarEvents ) ) {
                        retVal.push( `${dataContext}-${calendar.key}` );
                    }
                }
            }

            return retVal;
        },
        [
            calendarEvents,
            calendars,
            calendarsDataVersions,
            dataContext,
            dataStructure,
            globalDataVersions,
            queryType
        ]
    );


    useImperativeHandle(
        forwardedRef,
        () => ( {
            dirty            : () => dirtyDataContexts.current.length > 0,
            dirtyDataContexts: () => dirtyDataContexts.current
        } )
    );

    // init (on mount / unmount)
    useEffect(
        () => {
            return () => {
                if ( timeoutId.current ) {
                    clearTimeout( timeoutId.current );
                }
            };
        },
        []
    );

    // compute internal data / load data (on update) / initialize EventCalendar
    useEffect(
        () => {
            if ( viewKey ) {

                if ( !isLoading.current ) {
                    // load config
                    if ( !isConfigLoaded ) {
                        void loadConfig();
                    }
                    // load data
                    else if ( !isDataLoaded && viewStartDate && viewEndDate ) {
                        try {
                            void load();
                        }
                        catch ( e ) {
                            console.error( e );
                        }
                    }
                    // (re-)init
                    else if ( !( startDate && viewStartDate && viewEndDate ) && eventCalendar.current ) {
                        const view = eventCalendar.current?.getView();

                        if ( view ) {
                            onDatesSet( {
                                start   : view.activeStart,
                                startStr: view.activeStart.toISOString(),
                                end     : view.activeEnd,
                                endStr  : view.activeEnd.toISOString(),
                                view
                            } );
                        }
                    }
                }

                if ( isConfigLoaded && !eventCalendar.current ) {
                    const target = document.getElementById( `${uid}_wrapper` );

                    if ( target ) {
                        eventCalendar.current = createCalendar(
                            target,
                            [
                                TimeGrid,
                                DayGrid,
                                Interaction
                            ],
                            {
                                headerToolbar   : {
                                    start : 'prev,next today',
                                    center: 'title',
                                    end   : viewSelectors.join( ',' )
                                },
                                nowIndicator,
                                pointer         : true,
                                slotEventOverlap: false
                            }
                        );

                        setCalenderLanguageOptions();
                        setCalenderViewOptions();
                        setCalenderEditableOptions();
                    }
                }

                if ( elusiveData?.events !== events ) {
                    dispatch( setElusiveData( uid, { events } ) );
                }
            }
        }
    );

    // update language options
    useEffect(
        () => {
            setCalenderLanguageOptions();
        },
        [
            setCalenderLanguageOptions
        ]
    );

    // update view options
    useEffect(
        () => {
            setCalenderViewOptions();
        },
        [
            eventCalendar.current,
            setCalenderViewOptions
        ]
    );

    // update edit options
    useEffect(
        () => {
            setCalenderEditableOptions();
        },
        [
            eventCalendar.current,
            setCalenderEditableOptions
        ]
    );

    // update events
    useEffect(
        () => {
            if ( eventCalendar.current ) {
                eventCalendar.current.setOption( 'events', elusiveData?.events ?? [] );
            }
        },
        [
            elusiveData?.events ?? ''
        ]
    );

    // update background events
    useEffect(
        () => {
            if ( eventCalendar.current ) {
                eventCalendar.current.setOption( 'highlightedDates', Object.keys( backgroundEvents ) as string[] );
            }
        },
        [
            backgroundEvents
        ]
    );

    // update view
    useEffect(
        () => {
            if ( timeoutId.current ) {
                clearTimeout( timeoutId.current );
            }
            timeoutId.current = setTimeout( processBackgroundEvents, 11 );
        }
    );

    return content;
} );
Calendar.defaultProps = defaultProps;
Calendar.displayName = 'Calendar';

export default Calendar;
